/**
 * Timeline State Hook
 *
 * React hook managing timeline state with undo/redo history stack.
 * Handles all timeline editing operations: add, remove, reorder, split, trim,
 * speed control, copy/paste, and type-aware selection.
 */

import { useReducer, useCallback, useMemo, useEffect, useRef, useId } from 'react'
import type {
  TimelineState,
  TimelineClip,
  TimelineAudioClip,
  TimelineAction,
  TimelineHistoryEntry,
} from './types'
import {
  chooseExtractedNativeAudioTrackPlacement,
  computeTimelineDuration,
  extractedNativeAudioClipId,
  hasExtractedNativeAudioForVideo,
  isExtractedNativeAudioClip,
  preventSameTrackOverlaps,
  packMainTrack,
} from './timelineUtils'
import { summarizeSelection, splitTargetAtPlayhead } from './timelineSelection'
import { editClipToTimelineAudio, timelineAudioToEditClip } from '../../audio-edit/audioEditMath'
import { splitAudioClipWindow } from '../../shared/audioClipSplit'
import { MAX_VOLUME_AMPLITUDE } from './volumeMapping'
import { createNadDebug } from '../../../../utils/debugLog'
import {
  audioLabelWithPartSuffix,
  isBackgroundAudio,
  isOriginalAudio,
  isReplacementAudio,
} from './timelineAudioKind'

const timelineLog = createNadDebug('dw:timeline-playback:render-contract')
const overhaulLog = createNadDebug('dw:timeline-overhaul:edit')
const featureLog = createNadDebug('dw:video-editor-improvements')
const magnetLog = createNadDebug('dw:video-editor:magnet')
const splitFrameLog = createNadDebug('dw:video-editor:split-frame')
const trackMuteLog = createNadDebug('dw:video-editor:track-mute')
// RFC-0631 round-3 (Issue 5): per-track Remove button observability. Fires
// when a remove is requested, confirmed by the user, and applied to state.
const trackMgmtLog = createNadDebug('dw:video-editor:track-mgmt')
const timelineAudioActionLog = createNadDebug('dw:video-editor:timeline-audio-actions')
const timelineDialogueSplitLog = createNadDebug('dw:video-editor:dialogue-split')
const nativeAudioExtractLog = createNadDebug('dw:video-editor:native-audio-extract')
const timelineVisualPolishLog = createNadDebug('dw:timeline-visual-polish')
// 0-200% volume slider (display) backed by amplitude up to +24 dB. Reducer
// clamps every write so a Director or stale callback that exceeds the
// physical [0, MAX_VOLUME_AMPLITUDE] range shows up as truncation in the
// trace (helps catch future regressions).
const volumeReducerLog = createNadDebug('dw:timeline-volume:reducer')
const clipRemovalLog = createNadDebug('dw:video-editor:clip-removal')
const timelineQaLog = createNadDebug('dw:video-editor:timeline-qa')
const trimUndoLog = createNadDebug('dw:timeline-trim-undo')
const TRIM_UNDO_TRACE_ID = 'timeline-trim-undo'
// T279076566: distinguishes a history-PRESERVING refresh (REFRESH_PRESENT) from
// a history-RESETTING restore (RESTORE_STATE). Background/system refreshes must
// preserve the undo stack; only genuine loads/topology changes reset it.
const undoHistoryLog = createNadDebug('dw:timeline-undo:history')

/** Upper bound for clip / audio track stored amplitude (= +24 dB ≈ 15.85×). */
const MAX_VOLUME = MAX_VOLUME_AMPLITUDE

/** Default native fps used when source-fps probe is unsupported or pending. */
const DEFAULT_SOURCE_FPS = 30

function splitTraceId(clipId: string): string {
  return `timeline-split:${clipId}`
}

function dialogueSplitTraceId(clipId: string): string {
  return `timeline-dialogue-split:${clipId}`
}

type TrimPreviewBaselineClipType = 'video' | 'audio'

function isVideoTimelineClip(clip: TimelineClip | TimelineAudioClip): clip is TimelineClip {
  return 'order' in clip
}

function isAudioTimelineClip(clip: TimelineClip | TimelineAudioClip): clip is TimelineAudioClip {
  return !isVideoTimelineClip(clip)
}

export interface TimelineStateWithHistory {
  present: TimelineState
  past: TimelineHistoryEntry[]
  future: TimelineHistoryEntry[]
  editRevision?: number
  trimPreviewBaseline?: TimelineState | null
  trimPreviewBaselineClipId?: string | null
  trimPreviewBaselineClipType?: TrimPreviewBaselineClipType | null
}

const MAX_HISTORY_DEPTH = 50

/** Maximum number of video clips allowed on the timeline */
const MAX_CLIPS = 99

/**
 * Create initial timeline state
 */
function createInitialState(): TimelineState {
  return {
    clips: [],
    audioClips: [],
    playheadPosition: 0,
    zoomLevel: 50, // 50 pixels per second default
    isPlaying: false,
    selectedClipIds: new Set<string>(),
    totalDuration: 0,
    videoTrackCount: 1,
    audioTrackCount: 1,
    copiedClip: null,
    copiedClipType: null,
    mutedVideoTracks: new Set<number>(),
    mutedAudioTracks: new Set<number>(),
    magnetEnabled: true,
  }
}

function withDuration(state: TimelineState): TimelineState {
  return {
    ...state,
    totalDuration: Math.max(computeTimelineDuration(state.clips, state.audioClips),
      ...(state.textOverlays ?? []).map((item) => item.start_seconds + item.duration_seconds)),
  }
}

function withNonOverlappingVideo(state: TimelineState, editedClipId?: string): TimelineState {
  return withDuration({
    ...state,
    clips: preventSameTrackOverlaps(state.clips, editedClipId),
  })
}

function withNonOverlappingAudio(state: TimelineState, editedClipId?: string): TimelineState {
  return withDuration({
    ...state,
    audioClips: preventSameTrackOverlaps(state.audioClips, editedClipId),
  })
}

function withNonOverlappingTimeline(
  state: TimelineState,
  editedVideoClipId?: string,
  editedAudioClipId?: string
): TimelineState {
  return withDuration({
    ...state,
    clips: preventSameTrackOverlaps(state.clips, editedVideoClipId),
    audioClips: preventSameTrackOverlaps(state.audioClips, editedAudioClipId),
  })
}

function buildDialogueSplitVideoClips(
  clip: TimelineClip,
  segments: Array<{ start_s: number; end_s: number }>,
): TimelineClip[] {
  const duration = clip.effectiveDuration
  if (!Number.isFinite(duration) || duration <= 0) return [clip]
  const frameDur = 1 / (DEFAULT_SOURCE_FPS * Math.max(clip.speed, 0.0001))
  const snapBoundary = (value: number): number => {
    if (!Number.isFinite(value)) return Number.NaN
    if (value <= 0) return 0
    if (value >= duration) return duration
    return Math.floor(value / frameDur) * frameDur
  }
  const boundaries = [0, duration]
  for (const segment of segments) {
    boundaries.push(snapBoundary(segment.start_s))
    boundaries.push(snapBoundary(segment.end_s))
  }
  const sorted = boundaries
    .filter((value) => Number.isFinite(value) && value >= 0 && value <= duration)
    .sort((a, b) => a - b)
  const unique: number[] = []
  for (const value of sorted) {
    const previous = unique[unique.length - 1]
    if (previous === undefined || Math.abs(value - previous) > 1e-6) {
      unique.push(value)
    }
  }
  if (unique.length < 2) return [clip]
  const activeSourceDuration = clip.originalDuration - clip.trimStart - clip.trimEnd
  return unique.flatMap((start, index) => {
    const end = unique[index + 1]
    if (end === undefined || end - start <= 1e-6) return []
    const sourceStart = clip.trimStart + start * clip.speed
    const sourceEnd = clip.trimStart + end * clip.speed
    const trimEnd = clip.originalDuration - sourceEnd
    if (sourceStart < clip.trimStart - 1e-6 || sourceEnd > clip.trimStart + activeSourceDuration + 1e-6) {
      return []
    }
    return [{
      ...clip,
      id: `${clip.id}-dialogue-${index + 1}`,
      trimStart: Math.max(0, sourceStart),
      trimEnd: Math.max(0, trimEnd),
      effectiveDuration: end - start,
      startOffset: clip.startOffset + start,
      order: clip.order + index,
      label: `${clip.label} (${index + 1})`,
    }]
  })
}

function buildDialogueSplitAudioClips(
  clip: TimelineAudioClip,
  segments: Array<{ start_s: number; end_s: number }>,
): TimelineAudioClip[] {
  const duration = clip.effectiveDuration
  if (!Number.isFinite(duration) || duration <= 0) return [clip]
  const clampBoundary = (value: number): number => {
    if (!Number.isFinite(value)) return Number.NaN
    if (value <= 0) return 0
    if (value >= duration) return duration
    return value
  }
  const boundaries = [0, duration]
  for (const segment of segments) {
    boundaries.push(clampBoundary(segment.start_s))
    boundaries.push(clampBoundary(segment.end_s))
  }
  const sorted = boundaries
    .filter((value) => Number.isFinite(value) && value >= 0 && value <= duration)
    .sort((a, b) => a - b)
  const unique: number[] = []
  for (const value of sorted) {
    const previous = unique[unique.length - 1]
    if (previous === undefined || Math.abs(value - previous) > 1e-6) {
      unique.push(value)
    }
  }
  if (unique.length < 2) return [clip]
  const activeSourceDuration = clip.originalDuration - clip.trimStart - clip.trimEnd
  const splitClips = unique.flatMap((start, index) => {
    const end = unique[index + 1]
    if (end === undefined || end - start <= 1e-6) return []
    const sourceStart = clip.trimStart + start * clip.speed
    const sourceEnd = clip.trimStart + end * clip.speed
    const trimEnd = clip.originalDuration - sourceEnd
    if (sourceStart < clip.trimStart - 1e-6 || sourceEnd > clip.trimStart + activeSourceDuration + 1e-6) {
      return []
    }
    const startOffset = clip.startOffset + start
    const effectiveDuration = end - start
    return [{
      ...clip,
      id: `${clip.id}-dialogue-${index + 1}`,
      trimStart: Math.max(0, sourceStart),
      trimEnd: Math.max(0, trimEnd),
      effectiveDuration,
      startOffset,
      label: audioLabelWithPartSuffix(clip.label, index + 1),
      sourceVideoClipId: isReplacementAudio(clip) ? undefined : clip.sourceVideoClipId,
      plannedEndOffset: clip.audioKind === 'speech' && typeof clip.plannedEndOffset === 'number'
        ? startOffset + effectiveDuration
        : undefined,
    }]
  })
  return splitClips.map((splitClip, index) => ({
    ...splitClip,
    audioFadeInS: index === 0 && clip.audioFadeInS !== undefined
      ? Math.min(splitClip.effectiveDuration, clip.audioFadeInS)
      : undefined,
    audioFadeOutS: index === splitClips.length - 1 && clip.audioFadeOutS !== undefined
      ? Math.min(splitClip.effectiveDuration, clip.audioFadeOutS)
      : undefined,
  }))
}

function clampVolume(volume: number): number {
  return Math.max(0, Math.min(MAX_VOLUME, volume))
}

function updateAudioClipVolume(state: TimelineState, clipId: string, volume: number): TimelineState | null {
  const clipIndex = state.audioClips.findIndex((c) => c.id === clipId)
  if (clipIndex === -1) return null
  const audioClips = [...state.audioClips]
  audioClips[clipIndex] = {
    ...audioClips[clipIndex],
    volume,
  }
  return {
    ...state,
    audioClips,
  }
}

function updateVideoClipVolume(state: TimelineState, clipId: string, volume: number): TimelineState | null {
  const clipIndex = state.clips.findIndex((c) => c.id === clipId)
  if (clipIndex === -1) return null
  const clips = [...state.clips]
  clips[clipIndex] = {
    ...clips[clipIndex],
    volume,
  }
  return {
    ...state,
    clips,
  }
}

function withSyncedLinkedReplacementAudio(state: TimelineState): TimelineState {
  const videoWindows = new Map(
    state.clips.map((clip) => [
      clip.id,
      {
        startOffset: clip.startOffset,
      },
    ]),
  )
  let changed = false
  let audioClips = state.audioClips

  for (const originalClip of state.audioClips) {
    const currentIndex = audioClips.findIndex((clip) => clip.id === originalClip.id)
    if (currentIndex === -1) continue

    const currentClip = audioClips[currentIndex]
    if (!isReplacementAudio(currentClip) || !currentClip.sourceVideoClipId) continue

    const videoWindow = videoWindows.get(currentClip.sourceVideoClipId)
    if (!videoWindow) continue

    let nextClip = Math.abs(currentClip.startOffset - videoWindow.startOffset) > 1e-6
      ? moveAudioClipToStart(currentClip, videoWindow.startOffset)
      : currentClip
    if (nextClip.audioKind !== 'speech' && nextClip.plannedEndOffset !== undefined) {
      nextClip = { ...nextClip, plannedEndOffset: undefined }
    }
    const trackIndex = firstAvailableAudioTrack(
      audioClips.filter((clip) => clip.id !== currentClip.id),
      nextClip,
      nextClip.trackIndex,
    )
    if (trackIndex !== nextClip.trackIndex) {
      nextClip = { ...nextClip, trackIndex }
    }
    if (nextClip !== currentClip) {
      changed = true
      audioClips = audioClips.map((clip) => clip.id === currentClip.id ? nextClip : clip)
    }
  }

  return changed ? withDuration({ ...state, audioClips }) : state
}

function clearTrimPreviewBaseline(state: TimelineStateWithHistory): TimelineStateWithHistory {
  if (
    !state.trimPreviewBaseline &&
    !state.trimPreviewBaselineClipId &&
    !state.trimPreviewBaselineClipType
  ) {
    return state
  }

  return {
    ...state,
    trimPreviewBaseline: null,
    trimPreviewBaselineClipId: null,
    trimPreviewBaselineClipType: null,
  }
}

type TimelinePositionedClip = TimelineClip | TimelineAudioClip

function moveAudioClipToStart(clip: TimelineAudioClip, nextStartOffset: number): TimelineAudioClip {
  const startOffset = Math.max(0, nextStartOffset)
  const delta = startOffset - clip.startOffset
  const plannedEndOffset =
    clip.audioKind === 'speech' &&
    typeof clip.plannedEndOffset === 'number' &&
    Number.isFinite(clip.plannedEndOffset)
      ? Math.max(0, clip.plannedEndOffset + delta)
      : undefined
  return {
    ...clip,
    startOffset,
    plannedEndOffset,
  }
}

type TimelineAudioActionId = 'change_voice' | 'isolate_audio'

function timelineAudioClipEnd(clip: Pick<TimelineAudioClip, 'startOffset' | 'effectiveDuration'>): number {
  return Math.max(0, clip.startOffset) + Math.max(0, clip.effectiveDuration)
}

function timelineAudioClipsOverlap(
  left: Pick<TimelineAudioClip, 'startOffset' | 'effectiveDuration'>,
  right: Pick<TimelineAudioClip, 'startOffset' | 'effectiveDuration'>,
): boolean {
  return Math.max(0, left.startOffset) < timelineAudioClipEnd(right) - 1e-6 &&
    Math.max(0, right.startOffset) < timelineAudioClipEnd(left) - 1e-6
}

function firstAvailableAudioTrack(
  clips: readonly TimelineAudioClip[],
  candidate: TimelineAudioClip,
  preferredTrackIndex: number,
): number {
  let trackIndex = Math.max(0, preferredTrackIndex)
  while (
    clips.some((clip) =>
      clip.trackIndex === trackIndex && timelineAudioClipsOverlap(clip, candidate),
    )
  ) {
    trackIndex += 1
  }
  return trackIndex
}

function primaryDerivedAudioClip(
  actionId: TimelineAudioActionId,
  clips: readonly TimelineAudioClip[],
): TimelineAudioClip {
  const replacement = clips.find((clip) => isReplacementAudio(clip))
  if (replacement) return replacement
  if (actionId === 'isolate_audio') {
    const nonBackground = clips.find((clip) => !isBackgroundAudio(clip))
    if (nonBackground) return nonBackground
  }
  return clips[0]
}

function replaceAudioClipSource(
  target: TimelineAudioClip,
  derived: TimelineAudioClip,
): TimelineAudioClip {
  return {
    ...target,
    sourceNodeId: derived.sourceNodeId,
    sourcePath: derived.sourcePath,
    sourceRef: derived.sourceRef,
    label: derived.label ?? target.label,
    audioKind: derived.audioKind ?? target.audioKind,
    originalDuration: derived.originalDuration,
    trimStart: derived.trimStart,
    trimEnd: derived.trimEnd,
    effectiveDuration: derived.effectiveDuration,
    role: derived.role,
    speed: derived.speed,
    splitGroupId: derived.splitGroupId,
    peakDbfs: derived.peakDbfs,
    plannedEndOffset: derived.audioKind === 'speech' ? derived.plannedEndOffset : undefined,
  }
}

function normalizeBackgroundClip(
  background: TimelineAudioClip,
  source: Pick<TimelineAudioClip, 'startOffset'>,
  existing: readonly TimelineAudioClip[],
  preferredTrackIndex: number,
): TimelineAudioClip {
  const candidate = {
    ...background,
    startOffset: source.startOffset,
    plannedEndOffset: undefined,
    sourceVideoClipId: undefined,
  }
  return {
    ...candidate,
    trackIndex: firstAvailableAudioTrack(existing, candidate, preferredTrackIndex),
  }
}

function belongsToSameStemGroup(
  clip: TimelineAudioClip,
  sourceClipId: string,
  splitGroupId: string | undefined,
): boolean {
  return clip.id !== sourceClipId &&
    splitGroupId !== undefined &&
    clip.splitGroupId === splitGroupId &&
    isBackgroundAudio(clip)
}

function isVideoOwnedPrimaryAudio(clip: TimelineAudioClip, sourceVideoClipId: string): boolean {
  return clip.sourceVideoClipId === sourceVideoClipId &&
    (isOriginalAudio(clip) || isReplacementAudio(clip))
}

function deletedSpansByTrack<T extends TimelinePositionedClip>(
  clips: readonly T[],
  selectedClipIds: ReadonlySet<string>,
): Map<number, Array<{ start: number; end: number; id: string }>> {
  const spansByTrack = new Map<number, Array<{ start: number; end: number; id: string }>>()
  for (const clip of clips) {
    if (!selectedClipIds.has(clip.id)) continue
    const trackIndex = Math.max(0, clip.trackIndex)
    const spans = spansByTrack.get(trackIndex) ?? []
    spans.push({
      id: clip.id,
      start: Math.max(0, clip.startOffset),
      end: Math.max(0, clip.startOffset) + Math.max(0, clip.effectiveDuration),
    })
    spansByTrack.set(trackIndex, spans)
  }

  for (const spans of spansByTrack.values()) {
    spans.sort((a, b) => a.start - b.start || a.end - b.end)
  }
  return spansByTrack
}

function rippleCloseDeletedSpans<T extends TimelinePositionedClip>(
  clips: readonly T[],
  spansByTrack: ReadonlyMap<number, Array<{ start: number; end: number; id: string }>>,
  moveClip?: (clip: T, startOffset: number) => T,
): { clips: T[]; shiftedCount: number; totalShiftSeconds: number } {
  const shiftClip = moveClip ?? ((clip: T, startOffset: number) => ({
    ...clip,
    startOffset,
  }))
  let shiftedCount = 0
  let totalShiftSeconds = 0

  const nextClips = clips.map((clip) => {
    const spans = spansByTrack.get(Math.max(0, clip.trackIndex))
    if (!spans || spans.length === 0) return clip

    const shift = spans.reduce((sum, span) => {
      if (span.end <= clip.startOffset + 1e-6) {
        return sum + Math.max(0, span.end - span.start)
      }
      return sum
    }, 0)

    if (shift <= 1e-6) return clip
    shiftedCount += 1
    totalShiftSeconds += shift
    const nextStartOffset = Math.max(0, clip.startOffset - shift)
    return shiftClip(clip, nextStartOffset)
  })

  return { clips: nextClips, shiftedCount, totalShiftSeconds }
}




/**
 * Timeline reducer handling all state mutations
 */
export function timelineReducer(
  state: TimelineStateWithHistory,
  action: TimelineAction
): TimelineStateWithHistory {
  const isTrimPreviewLifecycleAction =
    action.type === 'TRIM_CLIP' ||
    action.type === 'TRIM_CLIP_PREVIEW' ||
    action.type === 'TRIM_AUDIO_CLIP' ||
    action.type === 'TRIM_AUDIO_CLIP_PREVIEW' ||
    // T279076566 (CodeRabbit): a background REFRESH_PRESENT can land mid trim
    // drag. Treat it as part of the trim lifecycle so the top-of-reducer guard
    // does NOT drop `trimPreviewBaseline`; REFRESH_PRESENT itself preserves and
    // rebases the baseline so the subsequent trim commit still records the
    // correct pre-drag undo baseline.
    action.type === 'REFRESH_PRESENT'

  // Preview state is valid only while a trim drag is still in flight.
  // If another reducer action interleaves, treat the preview as abandoned.
  if (state.trimPreviewBaseline && !isTrimPreviewLifecycleAction) {
    state = clearTrimPreviewBaseline(state)
  }

  const { present, past, future } = state
  const editRevision = state.editRevision ?? 0

  // Helper to push current state to history
  const pushHistory = (
    newState: TimelineState,
    description: string,
    historyState: TimelineState = present,
  ): TimelineStateWithHistory => {
    const newPast = [
      ...past,
      {
        state: historyState,
        description,
        timestamp: Date.now(),
      },
    ].slice(-MAX_HISTORY_DEPTH)

    if (historyState !== present) {
      trimUndoLog({
        event: 'history-pushed-from-trim-preview-baseline',
        trace_id: TRIM_UNDO_TRACE_ID,
        description,
        pastDepth: newPast.length,
      })
    }

    return {
      present: newState,
      past: newPast,
      future: [], // Clear future when new action performed
      editRevision: editRevision + 1,
      trimPreviewBaseline: null,
      trimPreviewBaselineClipId: null,
      trimPreviewBaselineClipType: null,
    }
  }

  switch (action.type) {
    case 'SET_TEXT_OVERLAYS':
      return pushHistory(withDuration({ ...present, textOverlays: action.overlays, textFormat: action.format }), 'Edit Text')
    case 'ADD_CLIP': {
      // Enforce 99-clip max
      if (present.clips.length >= MAX_CLIPS) {
        return state
      }
      const newClips = [...present.clips, action.clip].sort((a, b) => a.order - b.order)
      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(withNonOverlappingVideo(newState, action.clip.id), `Add clip: ${action.clip.label}`)
    }

    case 'REMOVE_CLIP': {
      const removedClip = present.clips.find((c) => c.id === action.clipId)
      if (!removedClip) return state
      const deletedClipIds = new Set<string>([action.clipId])
      const deletedSpans = deletedSpansByTrack(present.clips, deletedClipIds)
      const remainingClips = present.clips.filter((c) => c.id !== action.clipId)
      const shifted = rippleCloseDeletedSpans(remainingClips, deletedSpans)
      const reorderedClips = shifted.clips.map((clip, index) => ({ ...clip, order: index }))
      const nextSelected = new Set(present.selectedClipIds)
      nextSelected.delete(action.clipId)
      clipRemovalLog({
        event: 'clip-removed',
        clipId: action.clipId,
        sourceNodeId: removedClip.sourceNodeId,
        clipsBefore: present.clips.length,
        clipsAfter: reorderedClips.length,
      })
      timelineQaLog({
        event: 'ripple-remove-applied',
        clipId: action.clipId,
        videoTracksAffected: Array.from(deletedSpans.keys()),
        shiftedVideoClips: shifted.shiftedCount,
        totalShiftSeconds: Number(shifted.totalShiftSeconds.toFixed(3)),
      })
      const newState = {
        ...present,
        clips: reorderedClips,
        audioClips: present.audioClips,
        selectedClipIds: nextSelected,
      }
      return pushHistory(withDuration(newState), `Remove clip: ${action.clipId}`)
    }

    case 'REORDER_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const otherClips = present.clips.filter((c) => c.id !== action.clipId)

      // Insert at new position
      const newClips = [
        ...otherClips.slice(0, action.newOrder),
        clip,
        ...otherClips.slice(action.newOrder),
      ]

      // Update order indices
      const reorderedClips = newClips.map((c, index) => ({ ...c, order: index }))

      const newState = {
        ...present,
        clips: reorderedClips,
      }
      return pushHistory(newState, `Reorder clip: ${clip.label} to position ${action.newOrder}`)
    }

    case 'SPLIT_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) {
        splitFrameLog({
          event: 'split-rejected',
          trace_id: splitTraceId(action.clipId),
          clipType: 'video',
          clipId: action.clipId,
          reason: 'clip-not-found',
          splitTimeS: Number(action.splitTime.toFixed(3)),
        })
        return state
      }

      const clip = present.clips[clipIndex]
      const playheadPosition = action.splitTime

      // Calculate split position relative to clip start
      const rawRelativeTime = playheadPosition - clip.startOffset

      // RFC-0651 #4: snap the split to the source clip's native frame
      // boundary so the resulting cut lands cleanly on a frame edge.
      // Source windows are half-open: the first ends exactly where the
      // second begins. Do not discard a frame at the cut.
      const sourceFps = DEFAULT_SOURCE_FPS
      const frameDur = 1 / (sourceFps * clip.speed)
      const snappedRelativeTime = Math.floor(rawRelativeTime / frameDur) * frameDur
      const relativeTime = snappedRelativeTime
      const secondClipRelativeStart = relativeTime
      splitFrameLog({
        event: 'split-requested',
        trace_id: splitTraceId(action.clipId),
        clipType: 'video',
        clipId: action.clipId,
        sourceFps,
        playheadS: Number(playheadPosition.toFixed(3)),
        clipStartS: Number(clip.startOffset.toFixed(3)),
        effectiveDurationS: Number(clip.effectiveDuration.toFixed(3)),
        rawRelativeTimeS: Number(rawRelativeTime.toFixed(3)),
        snappedRelativeTimeS: Number(snappedRelativeTime.toFixed(3)),
      })

      // Validate split point is within clip bounds
      if (relativeTime <= 0 || relativeTime >= clip.effectiveDuration || secondClipRelativeStart >= clip.effectiveDuration) {
        splitFrameLog({
          event: 'split-rejected',
          trace_id: splitTraceId(action.clipId),
          clipType: 'video',
          clipId: action.clipId,
          reason: 'outside-valid-range',
          relativeTimeS: Number(relativeTime.toFixed(3)),
          secondClipRelativeStartS: Number(secondClipRelativeStart.toFixed(3)),
          effectiveDurationS: Number(clip.effectiveDuration.toFixed(3)),
        })
        return state
      }

      splitFrameLog({
        event: 'split-snapped',
        trace_id: splitTraceId(action.clipId),
        clipId: action.clipId,
        sourceFps,
        playhead: playheadPosition,
        rawRelativeTime,
        snappedRelativeTime,
      })

      // Create two new clips (speed-aware: convert relative time back to source time)
      const sourceRelativeTime = relativeTime * clip.speed

      const firstClip: TimelineClip = {
        ...clip,
        id: `${clip.id}-split-1`,
        trimEnd: clip.trimEnd + (clip.originalDuration - clip.trimStart - clip.trimEnd - sourceRelativeTime),
        effectiveDuration: relativeTime,
        label: `${clip.label} (1)`,
      }

      const secondClip: TimelineClip = {
        ...clip,
        id: `${clip.id}-split-2`,
        // Adjacent half-open source windows preserve every frame and sample.
        trimStart: clip.trimStart + sourceRelativeTime,
        effectiveDuration: clip.effectiveDuration - relativeTime,
        startOffset: clip.startOffset + relativeTime,
        order: clip.order + 1,
        label: `${clip.label} (2)`,
      }

      // Replace original clip with two new clips
      const newClips = [
        ...present.clips.slice(0, clipIndex),
        firstClip,
        secondClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      // Update order indices for clips after the split
      const reorderedClips = newClips.map((c, index) => ({ ...c, order: index }))

      const newState = {
        ...present,
        clips: reorderedClips,
      }
      splitFrameLog({
        event: 'split-applied',
        trace_id: splitTraceId(action.clipId),
        clipType: 'video',
        clipId: action.clipId,
        firstClipId: firstClip.id,
        secondClipId: secondClip.id,
        splitTimeS: Number(playheadPosition.toFixed(3)),
        firstDurationS: Number(firstClip.effectiveDuration.toFixed(3)),
        secondDurationS: Number(secondClip.effectiveDuration.toFixed(3)),
      })
      return pushHistory(withNonOverlappingVideo(newState, secondClip.id), `Split clip: ${clip.label} at ${playheadPosition}s`)
    }

    case 'SPLIT_CLIP_BY_DIALOGUE': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) {
        timelineDialogueSplitLog({
          event: 'split-rejected',
          trace_id: dialogueSplitTraceId(action.clipId),
          clipId: action.clipId,
          reason: 'clip-not-found',
        })
        return state
      }
      const clip = present.clips[clipIndex]
      const splitClips = buildDialogueSplitVideoClips(clip, action.segments)
      if (splitClips.length <= 1) {
        timelineDialogueSplitLog({
          event: 'split-rejected',
          trace_id: dialogueSplitTraceId(action.clipId),
          clipId: action.clipId,
          reason: 'no-valid-boundaries',
          segmentCount: action.segments.length,
        })
        return state
      }
      const newClips = [
        ...present.clips.slice(0, clipIndex),
        ...splitClips,
        ...present.clips.slice(clipIndex + 1),
      ]
      const reorderedClips = newClips.map((c, index) => ({ ...c, order: index }))
      const selectedClipIds = new Set(splitClips.map((splitClip) => splitClip.id))
      const newState = {
        ...present,
        clips: reorderedClips,
        selectedClipIds,
      }
      timelineDialogueSplitLog({
        event: 'split-applied',
        trace_id: dialogueSplitTraceId(action.clipId),
        clipId: action.clipId,
        segmentCount: action.segments.length,
        outputClipCount: splitClips.length,
        outputClipIds: splitClips.map((splitClip) => splitClip.id),
      })
      return pushHistory(
        withNonOverlappingVideo(newState, splitClips[splitClips.length - 1]?.id),
        `Split clip by dialogue: ${clip.label}`,
      )
    }

    case 'SPLIT_AUDIO_CLIP_BY_DIALOGUE': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) {
        timelineDialogueSplitLog({
          event: 'split-rejected',
          trace_id: dialogueSplitTraceId(action.clipId),
          clipId: action.clipId,
          clipType: 'audio',
          reason: 'clip-not-found',
        })
        return state
      }
      const clip = present.audioClips[clipIndex]
      const splitClips = buildDialogueSplitAudioClips(clip, action.segments)
      if (splitClips.length <= 1) {
        timelineDialogueSplitLog({
          event: 'split-rejected',
          trace_id: dialogueSplitTraceId(action.clipId),
          clipId: action.clipId,
          clipType: 'audio',
          reason: 'no-valid-boundaries',
          segmentCount: action.segments.length,
        })
        return state
      }
      const newAudioClips = [
        ...present.audioClips.slice(0, clipIndex),
        ...splitClips,
        ...present.audioClips.slice(clipIndex + 1),
      ]
      const selectedClipIds = new Set(splitClips.map((splitClip) => splitClip.id))
      const newState = {
        ...present,
        audioClips: newAudioClips,
        selectedClipIds,
      }
      timelineDialogueSplitLog({
        event: 'split-applied',
        trace_id: dialogueSplitTraceId(action.clipId),
        clipId: action.clipId,
        clipType: 'audio',
        segmentCount: action.segments.length,
        outputClipCount: splitClips.length,
        outputClipIds: splitClips.map((splitClip) => splitClip.id),
      })
      return pushHistory(
        withNonOverlappingAudio(newState, splitClips[splitClips.length - 1]?.id),
        `Split audio clip by dialogue: ${clip.id}`,
      )
    }

    case 'TRIM_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return clearTrimPreviewBaseline(state)

      const clip = present.clips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      // When trim-start drag couples startOffset (Issue 1), the handler passes
      // the new startOffset (may be temporarily negative — NORMALIZE_TIMELINE_ORIGIN
      // shifts it back on mouseUp).
      const startOffset = action.startOffset ?? clip.startOffset
      const newClip = {
        ...clip,
        trimStart,
        trimEnd,
        startOffset,
        effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      featureLog({
        event: 'trim-start-with-offset',
        clipId: action.clipId,
        newTrimStart: trimStart,
        newStartOffset: startOffset,
      })

      const newState = {
        ...present,
        clips: newClips,
      }
      const matchingTrimPreviewBaseline =
        state.trimPreviewBaseline &&
        state.trimPreviewBaselineClipId === action.clipId &&
        state.trimPreviewBaselineClipType === 'video'
          ? state.trimPreviewBaseline
          : null
      const historyState = matchingTrimPreviewBaseline ?? present
      if (matchingTrimPreviewBaseline) {
        trimUndoLog({
          event: 'trim-preview-baseline-consumed',
          trace_id: TRIM_UNDO_TRACE_ID,
          clipId: action.clipId,
          clipType: 'video',
        })
      }
      return pushHistory(
        withSyncedLinkedReplacementAudio(withNonOverlappingVideo(newState, action.clipId)),
        `Trim clip: ${clip.label} (start: ${action.trimStart}, end: ${action.trimEnd})`,
        historyState,
      )
    }

    case 'TRIM_CLIP_PREVIEW': {
      // Same as TRIM_CLIP but without pushing to history (used during drag)
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return clearTrimPreviewBaseline(state)

      const clip = present.clips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const startOffset = action.startOffset ?? clip.startOffset
      const newClip = {
        ...clip,
        trimStart,
        trimEnd,
        startOffset,
        effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      // Allow the edited clip's startOffset to go negative during drag;
      // NORMALIZE_TIMELINE_ORIGIN on mouseUp shifts the whole timeline right.
      const matchingTrimPreviewBaseline =
        state.trimPreviewBaseline &&
        state.trimPreviewBaselineClipId === action.clipId &&
        state.trimPreviewBaselineClipType === 'video'
          ? state.trimPreviewBaseline
          : null
      if (!matchingTrimPreviewBaseline) {
        trimUndoLog({
          event: 'trim-preview-baseline-captured',
          trace_id: TRIM_UNDO_TRACE_ID,
          clipId: action.clipId,
          clipType: 'video',
        })
      }
      return {
        ...state,
        trimPreviewBaseline: matchingTrimPreviewBaseline ?? present,
        trimPreviewBaselineClipId: action.clipId,
        trimPreviewBaselineClipType: 'video',
        present: {
          ...present,
          ...withSyncedLinkedReplacementAudio(withDuration({
            ...present,
            clips: preventSameTrackOverlaps(newClips, action.clipId, {
              allowNegativeForEditedClip: true,
            }),
          })),
        },
      }
    }

    case 'SET_CLIP_OFFSET': {
      const newClips = present.clips.map((clip) =>
        clip.id === action.clipId
          ? { ...clip, startOffset: Math.max(0, action.startOffset) }
          : clip
      )
      const videoState = withSyncedLinkedReplacementAudio(withNonOverlappingVideo({
        ...present,
        clips: newClips,
      }, action.clipId))
      return pushHistory(videoState, `Set video offset: ${action.startOffset}s`)
    }

    case 'SET_CLIP_TRACK_INDEX': {
      const trackIndex = Math.max(0, Math.min(present.videoTrackCount - 1, action.trackIndex))
      const newClips = present.clips.map((clip) =>
        clip.id === action.clipId ? { ...clip, trackIndex } : clip
      )
      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(withNonOverlappingVideo(newState, action.clipId), `Move video clip to track ${trackIndex + 1}`)
    }

    case 'SELECT_CLIP': {
      // Replace selection (existing behavior — non-shift click).
      const next = action.clipId === null
        ? new Set<string>()
        : new Set<string>([action.clipId])
      return {
        ...state,
        present: {
          ...present,
          selectedClipIds: next,
        },
      }
    }

    case 'TOGGLE_CLIP_SELECTION': {
      // Shift-click: add/remove the clip from the current selection.
      // Cross-type (video + audio in the same selection) is ALLOWED.
      const next = new Set(present.selectedClipIds)
      if (next.has(action.clipId)) {
        next.delete(action.clipId)
      } else {
        next.add(action.clipId)
      }
      return {
        ...state,
        present: {
          ...present,
          selectedClipIds: next,
        },
      }
    }

    case 'CLEAR_SELECTION': {
      if (present.selectedClipIds.size === 0) return state
      return {
        ...state,
        present: {
          ...present,
          selectedClipIds: new Set<string>(),
        },
      }
    }

    case 'SET_PLAYHEAD': {
      // Playhead movement doesn't go into history
      return {
        ...state,
        present: {
          ...present,
          playheadPosition: Math.max(0, Math.min(action.position, action.playbackDuration ?? present.totalDuration)),
        },
      }
    }

    case 'SET_ZOOM': {
      // Zoom doesn't go into history
      return {
        ...state,
        present: {
          ...present,
          zoomLevel: Math.max(10, Math.min(200, action.zoomLevel)), // 10-200 pixels per second
        },
      }
    }

    case 'PLAY': {
      return {
        ...state,
        present: {
          ...present,
          isPlaying: true,
        },
      }
    }

    case 'PAUSE': {
      return {
        ...state,
        present: {
          ...present,
          isPlaying: false,
        },
      }
    }

    case 'ADD_AUDIO_CLIP': {
      const newAudioClips = [...present.audioClips, action.clip]
      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clip.id), `Add audio clip`)
    }

    case 'ADD_AUDIO_CLIPS': {
      if (action.clips.length === 0) {
        return state
      }
      const newAudioClips = [...present.audioClips, ...action.clips]
      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(
        withNonOverlappingAudio(newState, action.clips[0].id),
        `Add ${action.clips.length} audio clip${action.clips.length === 1 ? '' : 's'}`
      )
    }

    case 'EXTRACT_NATIVE_VIDEO_AUDIO': {
      const sourceClip = present.clips.find((clip) => clip.id === action.clipId)
      if (!sourceClip) {
        nativeAudioExtractLog({
          event: 'native-audio-extract-skipped',
          reason: 'source-video-not-found',
          sourceClipId: action.clipId,
        })
        return state
      }
      if (hasExtractedNativeAudioForVideo(present.audioClips, sourceClip.id)) {
        nativeAudioExtractLog({
          event: 'native-audio-extract-skipped',
          reason: 'already-extracted',
          sourceClipId: sourceClip.id,
        })
        return state
      }

      const baseAudioClip: TimelineAudioClip = {
        id: extractedNativeAudioClipId(sourceClip.id),
        sourceNodeId: sourceClip.sourceNodeId,
        sourcePath: sourceClip.sourcePath,
        label: 'Original audio', // i18n-exempt: persisted timeline audio label, not direct UI copy.
        audioKind: 'extracted_original_audio',
        originalDuration: sourceClip.originalDuration,
        trimStart: sourceClip.trimStart,
        trimEnd: sourceClip.trimEnd,
        effectiveDuration: sourceClip.effectiveDuration,
        volume: sourceClip.volume,
        muted: sourceClip.muted,
        startOffset: sourceClip.startOffset,
        speed: sourceClip.speed,
        trackIndex: 0,
        sourceVideoClipId: sourceClip.id,
        audioFadeInS: sourceClip.audioFadeInS,
        audioFadeOutS: sourceClip.audioFadeOutS,
      }
      const placement = chooseExtractedNativeAudioTrackPlacement(present.audioClips, baseAudioClip)
      const shiftedAudioClips = placement.insertedOriginalTrack
        ? present.audioClips.map((clip) =>
            isExtractedNativeAudioClip(clip) || clip.trackIndex < placement.trackIndex
              ? clip
              : { ...clip, trackIndex: clip.trackIndex + 1 },
          )
        : present.audioClips
      const extractedClip: TimelineAudioClip = {
        ...baseAudioClip,
        trackIndex: placement.trackIndex,
      }
      const nextAudioClips = [...shiftedAudioClips, extractedClip]
      const maxAudioTrackIndex = nextAudioClips.reduce(
        (max, clip) => Math.max(max, clip.trackIndex),
        0,
      )
      const nextState = withDuration({
        ...present,
        clips: present.clips.map((clip) =>
          clip.id === sourceClip.id ? { ...clip, muted: true } : clip,
        ),
        audioClips: nextAudioClips,
        audioTrackCount: Math.max(present.audioTrackCount, maxAudioTrackIndex + 1, 1),
        selectedClipIds: new Set([extractedClip.id]),
      })
      nativeAudioExtractLog({
        event: 'native-audio-extract-applied',
        sourceClipId: sourceClip.id,
        extractedClipId: extractedClip.id,
        trackIndex: extractedClip.trackIndex,
        insertedOriginalTrack: placement.insertedOriginalTrack,
        audioTrackCount: nextState.audioTrackCount,
      })
      return pushHistory(nextState, 'Extract native audio')
    }

    case 'APPLY_DERIVED_AUDIO_CLIPS':
    case 'APPLY_DERIVED_AUDIO_IN_PLACE': {
      if (action.clips.length === 0) return state

      const actionId: TimelineAudioActionId =
        action.type === 'APPLY_DERIVED_AUDIO_IN_PLACE'
          ? action.actionId
          : action.clips.length > 1
            ? 'isolate_audio'
            : 'change_voice'
      const primaryClip = primaryDerivedAudioClip(actionId, action.clips)
      const backgroundClips = action.clips.filter((clip) => clip.id !== primaryClip.id)

      const sourceExists =
        action.sourceClipType === 'video'
          ? present.clips.some((clip) => clip.id === action.sourceClipId)
          : present.audioClips.some((clip) => clip.id === action.sourceClipId)
      if (!sourceExists) {
        timelineAudioActionLog({
          event: 'derived-audio-apply-skipped',
          reason: 'source-not-found',
          sourceClipId: action.sourceClipId,
          sourceClipType: action.sourceClipType,
          derivedClipCount: action.clips.length,
        })
        return state
      }

      let nextVideoClips = present.clips
      let nextAudioClips: TimelineAudioClip[] = []
      let selectedClipIds = new Set<string>([action.sourceClipId])

      if (action.sourceClipType === 'audio') {
        const sourceClip = present.audioClips.find((clip) => clip.id === action.sourceClipId)
        if (!sourceClip) return state

        const replacedSourceClip = replaceAudioClipSource(sourceClip, primaryClip)
        nextAudioClips = present.audioClips
          .filter((clip) => !belongsToSameStemGroup(clip, sourceClip.id, sourceClip.splitGroupId))
          .map((clip) => clip.id === action.sourceClipId ? replacedSourceClip : clip)
        let nextPreferredTrackIndex = sourceClip.trackIndex + 1
        for (const backgroundClip of backgroundClips) {
          const normalized = normalizeBackgroundClip(
            backgroundClip,
            replacedSourceClip,
            nextAudioClips,
            nextPreferredTrackIndex,
          )
          nextAudioClips.push(normalized)
          nextPreferredTrackIndex = normalized.trackIndex + 1
        }
      } else {
        const sourceVideoClip = present.clips.find((clip) => clip.id === action.sourceClipId)
        if (!sourceVideoClip) return state

        nextVideoClips = present.clips.map((clip) =>
          clip.id === action.sourceClipId ? { ...clip, muted: true } : clip,
        )
        const attachedReplacement: TimelineAudioClip = {
          ...primaryClip,
          label: primaryClip.label ?? (actionId === 'change_voice' ? 'Changed voice' : 'Isolated voice'),
          audioKind: primaryClip.audioKind ?? (actionId === 'change_voice' ? 'changed_voice' : 'isolated_voice'),
          role: primaryClip.role,
          sourceVideoClipId: sourceVideoClip.id,
          startOffset: sourceVideoClip.startOffset,
          plannedEndOffset: primaryClip.audioKind === 'speech' ? primaryClip.plannedEndOffset : undefined,
        }
        nextAudioClips = [
          ...present.audioClips.filter(
            (clip) =>
              !isVideoOwnedPrimaryAudio(clip, sourceVideoClip.id),
          ),
          attachedReplacement,
        ]
        let nextPreferredTrackIndex = attachedReplacement.trackIndex + 1
        for (const backgroundClip of backgroundClips) {
          const normalized = normalizeBackgroundClip(
            backgroundClip,
            attachedReplacement,
            nextAudioClips,
            nextPreferredTrackIndex,
          )
          nextAudioClips.push(normalized)
          nextPreferredTrackIndex = normalized.trackIndex + 1
        }
        selectedClipIds = new Set<string>([sourceVideoClip.id])
      }

      const maxDerivedTrackIndex = nextAudioClips.reduce(
        (max, clip) => Math.max(max, clip.trackIndex),
        present.audioTrackCount - 1,
      )
      const nextState = withNonOverlappingAudio({
        ...present,
        clips: nextVideoClips,
        audioClips: nextAudioClips,
        audioTrackCount: Math.max(present.audioTrackCount, maxDerivedTrackIndex + 1, 1),
        selectedClipIds,
      }, action.sourceClipType === 'audio' ? action.sourceClipId : primaryClip.id)
      timelineAudioActionLog({
        event: 'derived-audio-applied-in-place',
        sourceClipId: action.sourceClipId,
        sourceClipType: action.sourceClipType,
        actionId,
        primaryClipId: primaryClip.id,
        backgroundClipIds: backgroundClips.map((clip) => clip.id),
        derivedClipIds: action.clips.map((clip) => clip.id),
        audioTrackCount: nextState.audioTrackCount,
      })
      return pushHistory(
        nextState,
        action.description ?? `Apply derived audio clip${action.clips.length === 1 ? '' : 's'}`,
      )
    }

    case 'REMOVE_AUDIO_CLIP': {
      const newAudioClips = present.audioClips.filter((c) => c.id !== action.clipId)
      const nextSelected = new Set(present.selectedClipIds)
      nextSelected.delete(action.clipId)
      const newState = {
        ...present,
        audioClips: newAudioClips,
        selectedClipIds: nextSelected,
      }
      return pushHistory(withDuration(newState), `Remove audio clip`)
    }

    case 'SET_AUDIO_VOLUME': {
      const clamped = clampVolume(action.volume)
      volumeReducerLog({
        event: 'set-audio-volume',
        clipId: action.clipId,
        incoming: action.volume,
        clamped,
        truncated: clamped !== action.volume,
      })
      const newState = updateAudioClipVolume(present, action.clipId, clamped)
      if (!newState) return state
      return pushHistory(newState, `Set audio volume: ${action.volume}`)
    }

    case 'SET_AUDIO_VOLUME_PREVIEW': {
      const clamped = clampVolume(action.volume)
      const newState = updateAudioClipVolume(present, action.clipId, clamped)
      if (!newState) return state
      timelineVisualPolishLog({
        event: 'inline-volume-preview',
        clipId: action.clipId,
        clipType: 'audio',
        volume: Number(clamped.toFixed(3)),
      })
      return {
        ...state,
        present: newState,
      }
    }

    case 'COMMIT_AUDIO_VOLUME': {
      const clamped = clampVolume(action.volume)
      const baseline = clampVolume(action.baselineVolume)
      const newState = updateAudioClipVolume(present, action.clipId, clamped)
      const historyState = updateAudioClipVolume(present, action.clipId, baseline)
      if (!newState || !historyState) return state
      if (Math.abs(clamped - baseline) < 1e-4) return { ...state, present: newState }
      timelineVisualPolishLog({
        event: 'inline-volume-commit',
        clipId: action.clipId,
        clipType: 'audio',
        baseline: Number(baseline.toFixed(3)),
        volume: Number(clamped.toFixed(3)),
      })
      return pushHistory(newState, `Set audio volume: ${action.volume}`, historyState)
    }

    case 'TOGGLE_AUDIO_MUTE': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...newAudioClips[clipIndex],
        muted: !newAudioClips[clipIndex].muted,
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Toggle audio mute`)
    }

    case 'SET_AUDIO_OFFSET': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = moveAudioClipToStart(
        newAudioClips[clipIndex],
        action.startOffset,
      )

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Set audio offset: ${action.startOffset}s`)
    }

    case 'SET_AUDIO_TRACK_INDEX': {
      const trackIndex = Math.max(0, Math.min(present.audioTrackCount - 1, action.trackIndex))
      const newAudioClips = present.audioClips.map((clip) =>
        clip.id === action.clipId ? { ...clip, trackIndex } : clip
      )
      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Move audio clip to track ${trackIndex + 1}`)
    }

    case 'TRIM_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return clearTrimPreviewBaseline(state)

      const clip = present.audioClips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const editClip = timelineAudioToEditClip({
        ...clip,
        trimStart,
        trimEnd,
        startOffset: action.startOffset ?? clip.startOffset,
      })
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = editClipToTimelineAudio(editClip, clip)

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      const matchingTrimPreviewBaseline =
        state.trimPreviewBaseline &&
        state.trimPreviewBaselineClipId === action.clipId &&
        state.trimPreviewBaselineClipType === 'audio'
          ? state.trimPreviewBaseline
          : null
      const historyState = matchingTrimPreviewBaseline ?? present
      if (matchingTrimPreviewBaseline) {
        trimUndoLog({
          event: 'trim-preview-baseline-consumed',
          trace_id: TRIM_UNDO_TRACE_ID,
          clipId: action.clipId,
          clipType: 'audio',
        })
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Trim audio clip`, historyState)
    }

    case 'TRIM_AUDIO_CLIP_PREVIEW': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return clearTrimPreviewBaseline(state)

      const clip = present.audioClips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const editClip = timelineAudioToEditClip({
        ...clip,
        trimStart,
        trimEnd,
        startOffset: action.startOffset ?? clip.startOffset,
      })
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = editClipToTimelineAudio(editClip, clip)

      const matchingTrimPreviewBaseline =
        state.trimPreviewBaseline &&
        state.trimPreviewBaselineClipId === action.clipId &&
        state.trimPreviewBaselineClipType === 'audio'
          ? state.trimPreviewBaseline
          : null
      if (!matchingTrimPreviewBaseline) {
        trimUndoLog({
          event: 'trim-preview-baseline-captured',
          trace_id: TRIM_UNDO_TRACE_ID,
          clipId: action.clipId,
          clipType: 'audio',
        })
      }
      return {
        ...state,
        trimPreviewBaseline: matchingTrimPreviewBaseline ?? present,
        trimPreviewBaselineClipId: action.clipId,
        trimPreviewBaselineClipType: 'audio',
        present: {
          ...present,
          audioClips: newAudioClips,
          totalDuration: computeTimelineDuration(present.clips, newAudioClips),
        },
      }
    }

    case 'SET_CLIP_VISUALS': {
      const clips = present.clips.map((clip) => clip.id === action.clipId ? {
        ...clip,
        adjustments: action.adjustments ?? clip.adjustments,
        fadeColor: action.fadeColor ?? clip.fadeColor,
      } : clip)
      return pushHistory({ ...present, clips }, 'Adjust clip appearance')
    }

    case 'SET_TRANSITION': {
      const left = present.clips.find((clip) => clip.id === action.leftId)
      const right = present.clips.find((clip) => clip.id === action.rightId)
      if (!left || !right || left.trackIndex !== right.trackIndex
        || Math.abs(left.startOffset + left.effectiveDuration - right.startOffset) > 0.001) return state
      const duration = action.color ? Math.min(0.25, left.effectiveDuration / 2, right.effectiveDuration / 2) : 0
      const clips = present.clips.map((clip) => clip.id === left.id
        ? { ...clip, fadeOutS: duration, fadeColor: action.color ?? clip.fadeColor }
        : clip.id === right.id ? { ...clip, fadeInS: duration, fadeColor: action.color ?? clip.fadeColor } : clip)
      return pushHistory({ ...present, clips }, 'Change transition')
    }

    case 'REPLACE_CLIP_SOURCE': {
      if (!Number.isFinite(action.duration) || action.duration <= 0 || !action.sourcePath) {
        throw new Error('Time edit returned invalid media')
      }
      const clips = present.clips.map((clip) => clip.id === action.clipId ? {
        ...clip, sourcePath: action.sourcePath, sourceRef: action.sourcePath,
        // Each immutable render is a different source, including consecutive
        // time edits on the same UI clip. Metadata refresh must not cross them.
        sourceNodeId: `time-edit-${clip.id}-${action.sourcePath}`, originalDuration: action.duration,
        trimStart: 0, trimEnd: 0, effectiveDuration: action.duration / clip.speed,
        speedCurve: action.speedCurve,
      } : clip)
      let next = withSyncedLinkedReplacementAudio(withNonOverlappingVideo({ ...present, clips }, action.clipId))
      if (next.magnetEnabled && next.clips.find((clip) => clip.id === action.clipId)?.trackIndex === 0) {
        next = timelineReducer({ present: next, past: [], future: [], editRevision }, { type: 'MAGNET_PACK_TRACK0' }).present
      }
      next = withSyncedLinkedReplacementAudio(next)
      return pushHistory(next, action.mode === 'reverse' ? 'Reverse clip' : action.mode === 'freeze' ? 'Freeze frame' : 'Apply speed curve')
    }

    case 'SET_AUDIO_FADES': {
      if (![action.fadeIn, action.fadeOut].every((n) => Number.isFinite(n) && n >= 0)) throw new Error('Audio fades must be finite and nonnegative')
      const update = <T extends TimelineClip | TimelineAudioClip>(clip: T): T => clip.id === action.clipId
        ? { ...clip, audioFadeInS: Math.min(clip.effectiveDuration, action.fadeIn), audioFadeOutS: Math.min(clip.effectiveDuration, action.fadeOut) } : clip
      return pushHistory({ ...present, clips: present.clips.map(update), audioClips: present.audioClips.map(update) }, 'Adjust audio fades')
    }

    case 'SET_CLIP_SPEED': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const speed = Math.max(0.1, action.speed) // Minimum speed 0.1x
      const newClip = {
        ...clip,
        speed,
        effectiveDuration: (clip.originalDuration - clip.trimStart - clip.trimEnd) / speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      const changed = pushHistory(
        withSyncedLinkedReplacementAudio(withNonOverlappingVideo(newState, action.clipId)),
        `Set clip speed: ${speed}x`,
      )
      const result = present.magnetEnabled && clip.trackIndex === 0
        ? timelineReducer(changed, { type: 'MAGNET_PACK_TRACK0' })
        : changed
      return result
    }

    case 'SET_CLIP_VOLUME': {
      const clamped = clampVolume(action.volume)
      volumeReducerLog({
        event: 'set-clip-volume',
        clipId: action.clipId,
        incoming: action.volume,
        clamped,
        truncated: clamped !== action.volume,
      })
      const newState = updateVideoClipVolume(present, action.clipId, clamped)
      if (!newState) return state
      return pushHistory(newState, `Set video clip volume: ${action.volume}`)
    }

    case 'SET_CLIP_VOLUME_PREVIEW': {
      const clamped = clampVolume(action.volume)
      const newState = updateVideoClipVolume(present, action.clipId, clamped)
      if (!newState) return state
      timelineVisualPolishLog({
        event: 'inline-volume-preview',
        clipId: action.clipId,
        clipType: 'video',
        volume: Number(clamped.toFixed(3)),
      })
      return {
        ...state,
        present: newState,
      }
    }

    case 'COMMIT_CLIP_VOLUME': {
      const clamped = clampVolume(action.volume)
      const baseline = clampVolume(action.baselineVolume)
      const newState = updateVideoClipVolume(present, action.clipId, clamped)
      const historyState = updateVideoClipVolume(present, action.clipId, baseline)
      if (!newState || !historyState) return state
      if (Math.abs(clamped - baseline) < 1e-4) return { ...state, present: newState }
      timelineVisualPolishLog({
        event: 'inline-volume-commit',
        clipId: action.clipId,
        clipType: 'video',
        baseline: Number(baseline.toFixed(3)),
        volume: Number(clamped.toFixed(3)),
      })
      return pushHistory(newState, `Set video clip volume: ${action.volume}`, historyState)
    }

    case 'SET_CLIP_FADE_OUT': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      // Clamp to [0, effectiveDuration]. Effective duration is already speed-adjusted.
      const maxFade = Math.max(0, clip.effectiveDuration)
      const clamped = Math.max(0, Math.min(maxFade, action.fadeOutS))
      // No-op if value is unchanged to avoid pushing useless history entries.
      if (Math.abs(clamped - (clip.fadeOutS ?? 0)) < 1e-4) return state

      const newClip = {
        ...clip,
        fadeOutS: clamped,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(newState, `Set video clip fade-out: ${clamped}s`)
    }

    case 'SET_CLIP_FADE_IN': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const maxFade = Math.max(0, clip.effectiveDuration)
      const clamped = Math.max(0, Math.min(maxFade, action.fadeInS))
      if (Math.abs(clamped - (clip.fadeInS ?? 0)) < 1e-4) return state

      const newClip = {
        ...clip,
        fadeInS: clamped,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(newState, `Set video clip fade-in: ${clamped}s`)
    }

    case 'NORMALIZE_TIMELINE_ORIGIN': {
      // After a trim-start drag may have pulled a clip into negative time,
      // shift ALL clips (video AND audio) by the same delta so the leftmost
      // clip starts at 0. Single global pass — preserves cross-track A/V sync.
      let minStart = Infinity
      for (const c of present.clips) {
        if (c.startOffset < minStart) minStart = c.startOffset
      }
      for (const c of present.audioClips) {
        if (c.startOffset < minStart) minStart = c.startOffset
      }
      if (!Number.isFinite(minStart) || minStart >= 0) return state

      const shift = -minStart
      const newClips = present.clips.map((c) => ({ ...c, startOffset: c.startOffset + shift }))
      const newAudioClips = present.audioClips.map((c) =>
        moveAudioClipToStart(c, c.startOffset + shift)
      )

      featureLog({
        event: 'normalize-timeline-origin',
        shiftSeconds: Number(shift.toFixed(3)),
        affectedClipCount: newClips.length + newAudioClips.length,
      })

      const newState = {
        ...present,
        clips: newClips,
        audioClips: newAudioClips,
      }
      // Use the modified withDuration to capture the new total; do NOT push a
      // separate history entry — caller (TRIM_CLIP commit) already pushed one.
      return {
        ...state,
        present: withDuration(newState),
      }
    }

    case 'TOGGLE_CLIP_MUTE': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const newClip = {
        ...clip,
        muted: !clip.muted,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(newState, `Toggle video clip mute`)
    }

    case 'SET_AUDIO_SPEED': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const speed = Math.max(0.1, action.speed) // Minimum speed 0.1x
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...clip,
        speed,
        effectiveDuration: (clip.originalDuration - clip.trimStart - clip.trimEnd) / speed,
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Set audio speed: ${speed}x`)
    }

    case 'SPLIT_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) {
        splitFrameLog({
          event: 'split-rejected',
          trace_id: splitTraceId(action.clipId),
          clipType: 'audio',
          clipId: action.clipId,
          reason: 'clip-not-found',
          splitTimeS: Number(action.splitTime.toFixed(3)),
        })
        return state
      }

      const clip = present.audioClips[clipIndex]
      const splitTime = action.splitTime

      // Calculate relative split position within the audio clip
      const relativeTime = splitTime - clip.startOffset
      splitFrameLog({
        event: 'split-requested',
        trace_id: splitTraceId(action.clipId),
        clipType: 'audio',
        clipId: action.clipId,
        playheadS: Number(splitTime.toFixed(3)),
        clipStartS: Number(clip.startOffset.toFixed(3)),
        effectiveDurationS: Number(clip.effectiveDuration.toFixed(3)),
        relativeTimeS: Number(relativeTime.toFixed(3)),
      })

      // Validate split point is within clip bounds
      if (relativeTime <= 0 || relativeTime >= clip.effectiveDuration) {
        splitFrameLog({
          event: 'split-rejected',
          trace_id: splitTraceId(action.clipId),
          clipType: 'audio',
          clipId: action.clipId,
          reason: 'outside-valid-range',
          relativeTimeS: Number(relativeTime.toFixed(3)),
          effectiveDurationS: Number(clip.effectiveDuration.toFixed(3)),
        })
        return state
      }

      // RFC-1122 item C: split via the shared model-neutral helper (single source of truth with
      // the unified-audio Lanes editor). Map TimelineAudioClip → ClipWindow and back; the range
      // check above already guaranteed a strictly-interior split, so the helper returns non-null
      // (guard defensively anyway). Behaviour is byte-identical to the previous inline math:
      // sourceInS = trimStart, source seconds consumed by the first half = relativeTime * speed.
      const splitWindows = splitAudioClipWindow(
        {
          windowStartS: clip.startOffset,
          windowEndS: clip.startOffset + clip.effectiveDuration,
          sourceInS: clip.trimStart,
          speed: clip.speed,
        },
        splitTime,
      )
      if (!splitWindows) return state
      const { first, second } = splitWindows

      const firstClip: TimelineAudioClip = {
        ...clip,
        id: `${clip.id}-split-1`,
        label: audioLabelWithPartSuffix(clip.label, 1),
        trimEnd:
          clip.originalDuration -
          first.sourceInS -
          (first.windowEndS - first.windowStartS) * first.speed,
        effectiveDuration: first.windowEndS - first.windowStartS,
      }

      const secondClip: TimelineAudioClip = {
        ...clip,
        id: `${clip.id}-split-2`,
        label: audioLabelWithPartSuffix(clip.label, 2),
        trimStart: second.sourceInS,
        effectiveDuration: second.windowEndS - second.windowStartS,
        startOffset: second.windowStartS,
      }

      const newAudioClips = [
        ...present.audioClips.slice(0, clipIndex),
        firstClip,
        secondClip,
        ...present.audioClips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      splitFrameLog({
        event: 'split-applied',
        trace_id: splitTraceId(action.clipId),
        clipType: 'audio',
        clipId: action.clipId,
        firstClipId: firstClip.id,
        secondClipId: secondClip.id,
        splitTimeS: Number(splitTime.toFixed(3)),
        firstDurationS: Number(firstClip.effectiveDuration.toFixed(3)),
        secondDurationS: Number(secondClip.effectiveDuration.toFixed(3)),
      })
      return pushHistory(withNonOverlappingAudio(newState, secondClip.id), `Split audio clip at ${splitTime}s`)
    }

    case 'REORDER_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const otherClips = present.audioClips.filter((c) => c.id !== action.clipId)

      // Insert at new position
      const newAudioClips = [
        ...otherClips.slice(0, action.newOrder),
        clip,
        ...otherClips.slice(action.newOrder),
      ]

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Reorder audio clip to position ${action.newOrder}`)
    }

    case 'COPY_CLIP': {
      // Copy the primary (first) selected clip into the clipboard. Multi-clip
      // copy isn't supported in v1 — paste replays a single clip at the
      // playhead. Skip if nothing is selected.
      const summary = summarizeSelection(present)
      if (!summary.primaryId || summary.type === 'mixed' || summary.type === null) {
        return state
      }

      let clipToCopy: TimelineClip | TimelineAudioClip | null = null
      if (summary.type === 'video') {
        clipToCopy = present.clips.find((c) => c.id === summary.primaryId) ?? null
      } else {
        clipToCopy = present.audioClips.find((c) => c.id === summary.primaryId) ?? null
      }

      if (!clipToCopy) return state

      return {
        ...state,
        present: {
          ...present,
          copiedClip: { ...clipToCopy },
          copiedClipType: summary.type,
        },
      }
    }

    case 'PASTE_CLIP': {
      if (!present.copiedClip || !present.copiedClipType) {
        return state
      }

      if (present.copiedClipType === 'video' && isVideoTimelineClip(present.copiedClip)) {
        // Enforce 99-clip max
        if (present.clips.length >= MAX_CLIPS) {
          return state
        }

        const copiedVideoClip = present.copiedClip
        const newClip: TimelineClip = {
          ...copiedVideoClip,
          id: crypto.randomUUID(),
          order: present.clips.length,
          startOffset: present.playheadPosition,
          label: `${copiedVideoClip.label} (copy)`,
        }

        const newClips = [...present.clips, newClip].sort((a, b) => a.order - b.order)
        const newState = {
          ...present,
          clips: newClips,
        }
        return pushHistory(withNonOverlappingVideo(newState, newClip.id), `Paste clip: ${newClip.label}`)
      }

      if (present.copiedClipType === 'audio' && isAudioTimelineClip(present.copiedClip)) {
        const copiedAudioClip = present.copiedClip
        const pastedAudioClip = moveAudioClipToStart(copiedAudioClip, present.playheadPosition)
        const newClip: TimelineAudioClip = {
          ...pastedAudioClip,
          id: crypto.randomUUID(),
          label: `${(copiedAudioClip.label?.trim() || 'Audio')} (copy)`,
          sourceVideoClipId: undefined,
        }

        const newAudioClips = [...present.audioClips, newClip]
        const newState = {
          ...present,
          audioClips: newAudioClips,
        }
        return pushHistory(withNonOverlappingAudio(newState, newClip.id), `Paste audio clip`)
      }

      return state
    }

    case 'DELETE_SELECTED_CLIP': {
      if (present.selectedClipIds.size === 0) return state

      const removesVideoClip = present.clips.some((clip) => present.selectedClipIds.has(clip.id))
      const removesAudioClip = present.audioClips.some((clip) => present.selectedClipIds.has(clip.id))
      if (!removesVideoClip && !removesAudioClip) return state

      const videoDeletedSpans = deletedSpansByTrack(present.clips, present.selectedClipIds)
      const audioDeletedSpans = deletedSpansByTrack(present.audioClips, present.selectedClipIds)
      const remainingVideoClips = present.clips.filter((c) => !present.selectedClipIds.has(c.id))
      const remainingAudioClips = present.audioClips.filter((c) => !present.selectedClipIds.has(c.id))
      const shiftedVideo = rippleCloseDeletedSpans(remainingVideoClips, videoDeletedSpans)
      const shiftedAudio = rippleCloseDeletedSpans(remainingAudioClips, audioDeletedSpans, moveAudioClipToStart)
      const reorderedClips = shiftedVideo.clips.map((clip, index) => ({ ...clip, order: index }))
      clipRemovalLog({
        event: 'selected-clips-deleted',
        selectedIds: Array.from(present.selectedClipIds),
        videoClipsBefore: present.clips.length,
        videoClipsAfter: reorderedClips.length,
        audioClipsBefore: present.audioClips.length,
        audioClipsAfter: shiftedAudio.clips.length,
      })
      timelineQaLog({
        event: 'ripple-delete-applied',
        selectedIds: Array.from(present.selectedClipIds),
        videoTracksAffected: Array.from(videoDeletedSpans.keys()),
        audioTracksAffected: Array.from(audioDeletedSpans.keys()),
        shiftedVideoClips: shiftedVideo.shiftedCount,
        shiftedAudioClips: shiftedAudio.shiftedCount,
        totalShiftSeconds: Number((shiftedVideo.totalShiftSeconds + shiftedAudio.totalShiftSeconds).toFixed(3)),
      })
      const newState = {
        ...present,
        clips: reorderedClips,
        audioClips: shiftedAudio.clips,
        selectedClipIds: new Set<string>(),
      }
      return pushHistory(
        withSyncedLinkedReplacementAudio(withNonOverlappingTimeline(newState)),
        `Delete ${present.selectedClipIds.size} selected clip(s) and close affected gap(s)`,
      )
    }

    case 'RIPPLE_DELETE_SELECTED':
    case 'CLOSE_ALL_GAPS': {
      // Per-track gap closure (Issue 3). For every track (video AND audio),
      // sort clips by startOffset and pack them flush left-to-right starting
      // from 0. No clip is deleted. Cross-track sync may not be preserved
      // (each track collapses independently — by spec).
      const closeGapsOnTrack = <T extends { startOffset: number; effectiveDuration: number; trackIndex: number }>(
        clips: readonly T[],
        moveClip: (clip: T, startOffset: number) => T = (clip, startOffset) => ({
          ...clip,
          startOffset,
        }),
      ): { next: T[]; gapsClosed: number } => {
        if (clips.length === 0) return { next: [], gapsClosed: 0 }
        const sorted = [...clips].sort((a, b) => a.startOffset - b.startOffset)
        const next: T[] = []
        let cursor = 0
        let gapsClosed = 0
        for (const c of sorted) {
          if (c.startOffset > cursor + 1e-6) {
            next.push(moveClip(c, cursor))
            gapsClosed += 1
          } else {
            next.push(c)
          }
          cursor = (next[next.length - 1].startOffset) + c.effectiveDuration
        }
        return { next, gapsClosed }
      }

      const groupByTrack = <T extends { trackIndex: number }>(clips: readonly T[]): Map<number, T[]> => {
        const out = new Map<number, T[]>()
        for (const c of clips) {
          const arr = out.get(c.trackIndex) ?? []
          arr.push(c)
          out.set(c.trackIndex, arr)
        }
        return out
      }

      const perTrackGapsClosed: Record<string, number> = {}
      let totalGapsClosed = 0

      const videoByTrack = groupByTrack(present.clips)
      const newVideoClips: TimelineClip[] = []
      for (const [trackIndex, trackClips] of videoByTrack.entries()) {
        const { next, gapsClosed } = closeGapsOnTrack(trackClips)
        if (gapsClosed > 0) {
          perTrackGapsClosed[`v${trackIndex}`] = gapsClosed
          totalGapsClosed += gapsClosed
        }
        newVideoClips.push(...next)
      }

      const audioByTrack = groupByTrack(present.audioClips)
      const newAudioClips: TimelineAudioClip[] = []
      for (const [trackIndex, trackClips] of audioByTrack.entries()) {
        const { next, gapsClosed } = closeGapsOnTrack(
          trackClips,
          moveAudioClipToStart,
        )
        if (gapsClosed > 0) {
          perTrackGapsClosed[`a${trackIndex}`] = gapsClosed
          totalGapsClosed += gapsClosed
        }
        newAudioClips.push(...next)
      }

      // Clip count invariant: must not change.
      const clipCountBefore = present.clips.length + present.audioClips.length
      const clipCountAfter = newVideoClips.length + newAudioClips.length

      featureLog({
        event: 'close-all-gaps-applied',
        perTrackGapsClosed,
        totalGapsClosed,
        clipCountBefore,
        clipCountAfter,
      })

      // Preserve per-clip order across the array (we grouped by track).
      const newVideoById = new Map(newVideoClips.map((c) => [c.id, c]))
      const reassembledVideo = present.clips.map((c) => newVideoById.get(c.id) ?? c)
      const newAudioById = new Map(newAudioClips.map((c) => [c.id, c]))
      const reassembledAudio = present.audioClips.map((c) => newAudioById.get(c.id) ?? c)

      const newState = {
        ...present,
        clips: reassembledVideo,
        audioClips: reassembledAudio,
      }

      if (totalGapsClosed === 0) {
        return state
      }

      return pushHistory(
        withSyncedLinkedReplacementAudio(withNonOverlappingTimeline(newState)),
        `Close all gaps (${totalGapsClosed} gap(s))`,
      )
    }

    case 'MULTI_DRAG_OFFSET': {
      if (present.selectedClipIds.size === 0) return state

      const { deltaSec, trackDelta } = action
      // Compute the minimum startOffset across selected clips so we can clamp
      // delta so no clip goes negative — every clip moves by exactly the same
      // amount, preserving inter-clip gaps within the multi-selection.
      let minStart = Infinity
      const collect = (s: number) => {
        if (s < minStart) minStart = s
      }
      for (const c of present.clips) {
        if (present.selectedClipIds.has(c.id)) collect(c.startOffset)
      }
      for (const c of present.audioClips) {
        if (present.selectedClipIds.has(c.id)) collect(c.startOffset)
      }
      if (!Number.isFinite(minStart)) return state

      const clampedDelta = Math.max(deltaSec, -minStart)

      const td = trackDelta ?? 0
      const newClips = present.clips.map((c) => {
        if (!present.selectedClipIds.has(c.id)) return c
        const nextTrack = td !== 0
          ? Math.max(0, Math.min(present.videoTrackCount - 1, c.trackIndex + td))
          : c.trackIndex
        return {
          ...c,
          startOffset: Math.max(0, c.startOffset + clampedDelta),
          trackIndex: nextTrack,
        }
      })
      const newAudioClips = present.audioClips.map((c) => {
        if (!present.selectedClipIds.has(c.id)) return c
        const nextTrack = td !== 0
          ? Math.max(0, Math.min(present.audioTrackCount - 1, c.trackIndex + td))
          : c.trackIndex
        return {
          ...moveAudioClipToStart(c, c.startOffset + clampedDelta),
          trackIndex: nextTrack,
        }
      })

      const newState = {
        ...present,
        clips: newClips,
        audioClips: newAudioClips,
      }
      timelineLog({
        event: 'multi-drag-commit',
        clipIds: Array.from(present.selectedClipIds),
        deltaSec: Number(clampedDelta.toFixed(3)),
        trackDelta: td,
      })
      return pushHistory(
        withSyncedLinkedReplacementAudio(withNonOverlappingTimeline(newState)),
        `Multi-drag ${present.selectedClipIds.size} clip(s) by ${clampedDelta.toFixed(2)}s`,
      )
    }

    case 'ADD_VIDEO_TRACK': {
      return pushHistory(
        {
          ...present,
          videoTrackCount: present.videoTrackCount + 1,
        },
        `Add video track`
      )
    }

    case 'ADD_AUDIO_TRACK': {
      return pushHistory(
        {
          ...present,
          audioTrackCount: present.audioTrackCount + 1,
        },
        `Add audio track`
      )
    }

    // RFC-0631 round-3 (Issue 5): remove a video sub-track. Track 0 (Main
    // track) is protected; the count never drops below 1. When the track
    // has clips and `deleteClips` is true, those clips are dropped; when
    // false, the action is a safe no-op. Higher tracks shift down by 1 and
    // the muted-track set is re-indexed in the same step.
    case 'REMOVE_VIDEO_TRACK': {
      if (action.trackIndex === 0) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'video',
          trackIndex: action.trackIndex,
          reason: 'main-track-protected',
        })
        return state
      }
      if (present.videoTrackCount <= 1) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'video',
          trackIndex: action.trackIndex,
          reason: 'last-track',
        })
        return state
      }
      const targetClips = present.clips.filter((c) => c.trackIndex === action.trackIndex)
      if (targetClips.length > 0 && !action.deleteClips) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'video',
          trackIndex: action.trackIndex,
          reason: 'clips-present-without-confirm',
          clipCount: targetClips.length,
        })
        return state
      }
      const filteredClips = action.deleteClips
        ? present.clips.filter((c) => c.trackIndex !== action.trackIndex)
        : present.clips
      const shiftedClips = filteredClips.map((c) =>
        c.trackIndex > action.trackIndex ? { ...c, trackIndex: c.trackIndex - 1 } : c
      )
      const nextMuted = new Set<number>()
      for (const idx of present.mutedVideoTracks) {
        if (idx === action.trackIndex) continue
        nextMuted.add(idx > action.trackIndex ? idx - 1 : idx)
      }
      const newTrackCount = present.videoTrackCount - 1
      trackMgmtLog({
        event: 'track-remove-applied',
        kind: 'video',
        trackIndex: action.trackIndex,
        deleteClips: action.deleteClips,
        clipCount: targetClips.length,
        newTrackCount,
      })
      return pushHistory(
        withDuration({
          ...present,
          clips: shiftedClips,
          videoTrackCount: newTrackCount,
          mutedVideoTracks: nextMuted,
        }),
        `Remove video track ${action.trackIndex + 1}`
      )
    }

    case 'REMOVE_AUDIO_TRACK': {
      if (present.audioTrackCount <= 1) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'audio',
          trackIndex: action.trackIndex,
          reason: 'last-track',
        })
        return state
      }
      const targetClips = present.audioClips.filter((c) => c.trackIndex === action.trackIndex)
      if (targetClips.length > 0 && !action.deleteClips) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'audio',
          trackIndex: action.trackIndex,
          reason: 'clips-present-without-confirm',
          clipCount: targetClips.length,
        })
        return state
      }
      const filteredClips = action.deleteClips
        ? present.audioClips.filter((c) => c.trackIndex !== action.trackIndex)
        : present.audioClips
      const shiftedClips = filteredClips.map((c) =>
        c.trackIndex > action.trackIndex ? { ...c, trackIndex: c.trackIndex - 1 } : c
      )
      const nextMuted = new Set<number>()
      for (const idx of present.mutedAudioTracks) {
        if (idx === action.trackIndex) continue
        nextMuted.add(idx > action.trackIndex ? idx - 1 : idx)
      }
      const newTrackCount = present.audioTrackCount - 1
      trackMgmtLog({
        event: 'track-remove-applied',
        kind: 'audio',
        trackIndex: action.trackIndex,
        deleteClips: action.deleteClips,
        clipCount: targetClips.length,
        newTrackCount,
      })
      return pushHistory(
        withDuration({
          ...present,
          audioClips: shiftedClips,
          audioTrackCount: newTrackCount,
          mutedAudioTracks: nextMuted,
        }),
        `Remove audio track ${action.trackIndex + 1}`
      )
    }

    case 'CUT_AT_PLAYHEAD': {
      const target = splitTargetAtPlayhead(present)
      if (!target) return state
      const sourceDelta = (present.playheadPosition - target.clip.startOffset) * target.clip.speed
      const pack = present.magnetEnabled && target.type === 'video' && target.clip.trackIndex === 0
      const updateVideoClip = (clip: TimelineClip): TimelineClip => {
        if (target.type !== 'video' || clip.id !== target.clip.id) return clip
        const trimStart = action.side === 'left' ? clip.trimStart + sourceDelta : clip.trimStart
        const trimEnd = action.side === 'right' ? clip.originalDuration - clip.trimStart - sourceDelta : clip.trimEnd
        return { ...clip, trimStart, trimEnd, effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
          startOffset: action.side === 'left' && !pack ? present.playheadPosition : clip.startOffset }
      }
      const updateAudioClip = (clip: TimelineAudioClip): TimelineAudioClip => {
        if (target.type !== 'audio' || clip.id !== target.clip.id) return clip
        const trimStart = action.side === 'left' ? clip.trimStart + sourceDelta : clip.trimStart
        const trimEnd = action.side === 'right' ? clip.originalDuration - clip.trimStart - sourceDelta : clip.trimEnd
        const updated = {
          ...clip,
          trimStart,
          trimEnd,
          effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
          startOffset: action.side === 'left' ? present.playheadPosition : clip.startOffset,
          plannedEndOffset: clip.audioKind === 'speech' ? clip.plannedEndOffset : undefined,
        }
        if (
          action.side === 'left' &&
          isReplacementAudio(updated) &&
          updated.sourceVideoClipId
        ) {
          return { ...updated, sourceVideoClipId: undefined }
        }
        return updated
      }
      let next = withSyncedLinkedReplacementAudio(withDuration({
        ...present,
        clips: present.clips.map(updateVideoClip),
        audioClips: present.audioClips.map(updateAudioClip),
      }))
      if (pack) next = timelineReducer({ present: next, past: [], future: [], editRevision }, { type: 'MAGNET_PACK_TRACK0' }).present
      next = withSyncedLinkedReplacementAudio(next)
      return pushHistory(next, `Delete ${action.side} of playhead`)
    }

    case 'SPLIT_SELECTED_CLIP': {
      const target = splitTargetAtPlayhead(present)
      if (!target) return state
      return timelineReducer(state, {
        type: target.type === 'video' ? 'SPLIT_CLIP' : 'SPLIT_AUDIO_CLIP',
        clipId: target.clip.id,
        splitTime: present.playheadPosition,
      })
    }

    /* Selection is optional for splitting; target resolution is shared with the toolbar. */
    case 'TOGGLE_TRACK_MUTE': {
      const { trackKind, trackIndex } = action
      const sourceSet = trackKind === 'video' ? present.mutedVideoTracks : present.mutedAudioTracks
      const next = new Set(sourceSet)
      if (next.has(trackIndex)) next.delete(trackIndex)
      else next.add(trackIndex)
      trackMuteLog({
        event: 'toggle-track-mute',
        trackKind,
        trackIndex,
        muted: next.has(trackIndex),
      })
      const newPresent: TimelineState =
        trackKind === 'video'
          ? { ...present, mutedVideoTracks: next }
          : { ...present, mutedAudioTracks: next }
      return pushHistory(newPresent, `Toggle ${trackKind} track ${trackIndex} mute`)
    }

    case 'SET_MAGNET': {
      if (present.magnetEnabled === action.enabled) return state
      magnetLog({ event: 'magnet-toggle', enabled: action.enabled })
      // Persistent-but-non-historical: skip the history push so undo doesn't
      // toggle the magnet button (matches typical app-preference behavior).
      return { ...state, present: { ...present, magnetEnabled: action.enabled }, editRevision: editRevision + 1 }
    }

    case 'MAGNET_PACK_TRACK0': {
      const newClips = packMainTrack(present.clips)
      const movedCount = newClips.filter((clip, index) => clip !== present.clips[index]).length
      if (movedCount === 0) return state
      magnetLog({ event: 'magnet-pack-track0', clipsPackedCount: movedCount,
        before: present.clips.map((clip) => [clip.id, clip.startOffset]),
        after: newClips.map((clip) => [clip.id, clip.startOffset]),
      })
      // Skip history: magnet pack is an automatic follow-on to whatever
      // history-pushing op just triggered it (trim / move / split commit).
      return {
        ...state,
        present: withSyncedLinkedReplacementAudio(withDuration({ ...present, clips: newClips })),
      }
    }

    case 'UNDO': {
      if (past.length === 0) return state

      const previous = past[past.length - 1]
      const newPast = past.slice(0, -1)
      const newFuture = [
        {
          state: present,
          description: 'undo',
          timestamp: Date.now(),
        },
        ...future,
      ]

      return {
        present: previous.state,
        past: newPast,
        future: newFuture,
        editRevision: editRevision + 1,
        trimPreviewBaseline: null,
        trimPreviewBaselineClipId: null,
        trimPreviewBaselineClipType: null,
      }
    }

    case 'REDO': {
      if (future.length === 0) return state

      const next = future[0]
      const newFuture = future.slice(1)
      const newPast = [
        ...past,
        {
          state: present,
          description: 'redo',
          timestamp: Date.now(),
        },
      ]

      return {
        present: next.state,
        past: newPast,
        future: newFuture,
        editRevision: editRevision + 1,
        trimPreviewBaseline: null,
        trimPreviewBaselineClipId: null,
        trimPreviewBaselineClipType: null,
      }
    }

    case 'RESTORE_STATE': {
      // Used for loading from Yjs, initial state, or genuine edge-topology
      // changes. Deliberately RESETS undo/redo history: after a fresh load or a
      // clip being added/pruned from edges, resurrecting a pre-load timeline via
      // undo would be incoherent.
      undoHistoryLog({
        event: 'restore-state',
        reason: 'history-reset',
        pastDepthBefore: past.length,
        pastDepthAfter: 0,
      })
      return {
        present: withDuration(action.state),
        past: [],
        future: [],
        editRevision: action.preserveEditRevision ? editRevision : 0,
        trimPreviewBaseline: null,
        trimPreviewBaselineClipId: null,
        trimPreviewBaselineClipType: null,
      }
    }

    case 'REFRESH_PRESENT': {
      // T279076566: history-PRESERVING replacement of `present`. Used by
      // background/system refreshes (async duration probe, metadata-only edge
      // ref refresh) that update clip metadata WITHOUT changing timeline
      // topology. Unlike RESTORE_STATE, this keeps `past`/`future` intact so the
      // user's undo/redo stack survives these refreshes and the toolbar Undo
      // button + Cmd/Ctrl+Z stay responsive mid-session.
      const refreshedPresent = withDuration({ ...action.state,
        clips: action.reconcileMagnet && action.state.magnetEnabled ? packMainTrack(action.state.clips) : action.state.clips,
      })
      // Rebase the SOURCE-level metadata the refresh just corrected — asset path,
      // label, and originalDuration — into every preserved history snapshot.
      // Without this, a later undo/redo could resurrect a stale sourcePath/label
      // (e.g. after a clip was regenerated) or the placeholder originalDuration
      // (before the async duration probe resolved). Edit-owned fields (trim,
      // offset, order, volume, speed, fades, …) are preserved verbatim so
      // undo/redo still reverts the user's actual edits; effectiveDuration is
      // recomputed from the snapshot's OWN trim/speed against the corrected
      // originalDuration, and the whole snapshot is re-normalized so an undo
      // lands on a coherent, non-overlapping state.
      //
      // Rebase is keyed by clip `id` first (unique per clip). Clips can SHARE a
      // sourceNodeId (paste reuses the source with a fresh id; extracted audio
      // reuses the source video's id), so a sourceNodeId is only used as a
      // fallback when it maps to exactly ONE clip in the refreshed present.
      type VideoRef = { sourceNodeId: string; sourcePath: string; label: string; originalDuration: number }
      type AudioRef = {
        sourceNodeId: string
        sourcePath: string
        label?: string
        audioKind?: TimelineAudioClip['audioKind']
        originalDuration: number
      }
      const videoRefById = new Map<string, VideoRef>()
      const videoRefBySourceUnique = new Map<string, VideoRef | null>()
      for (const clip of refreshedPresent.clips) {
        videoRefById.set(clip.id, {
          sourceNodeId: clip.sourceNodeId,
          sourcePath: clip.sourcePath,
          label: clip.label,
          originalDuration: clip.originalDuration,
        })
        videoRefBySourceUnique.set(
          clip.sourceNodeId,
          videoRefBySourceUnique.has(clip.sourceNodeId)
            ? null // sourceNodeId maps to >1 clip → ambiguous, do not use as fallback
            : { sourceNodeId: clip.sourceNodeId, sourcePath: clip.sourcePath, label: clip.label, originalDuration: clip.originalDuration },
        )
      }
      const audioRefById = new Map<string, AudioRef>()
      const audioRefBySourceUnique = new Map<string, AudioRef | null>()
      for (const clip of refreshedPresent.audioClips) {
        audioRefById.set(clip.id, {
          sourceNodeId: clip.sourceNodeId,
          sourcePath: clip.sourcePath,
          label: clip.label,
          audioKind: clip.audioKind,
          originalDuration: clip.originalDuration,
        })
        audioRefBySourceUnique.set(
          clip.sourceNodeId,
          audioRefBySourceUnique.has(clip.sourceNodeId)
            ? null
            : {
                sourceNodeId: clip.sourceNodeId,
                sourcePath: clip.sourcePath,
                label: clip.label,
                audioKind: clip.audioKind,
                originalDuration: clip.originalDuration,
              },
        )
      }
      const matchingRef = <T extends { sourceNodeId: string }>(
        clip: { id: string; sourceNodeId: string }, byId: Map<string, T>, bySource: Map<string, T | null>,
      ): T | null => {
        const byClipId = byId.get(clip.id)
        // A stable UI id can now refer to a different (time-edited) asset.
        return byClipId?.sourceNodeId === clip.sourceNodeId
          ? byClipId
          : bySource.get(clip.sourceNodeId) ?? null
      }
      const recomputeEffectiveDuration = (
        originalDuration: number,
        trimStart: number,
        trimEnd: number,
        speed: number,
      ): number => {
        const safeSpeed = speed && speed > 0 ? speed : 1
        return Math.max(0, originalDuration - trimStart - trimEnd) / safeSpeed
      }
      // T279076566 (CodeRabbit): `copiedClip` is part of TimelineState and is
      // restored by undo/redo. If it carries a stale sourcePath/originalDuration,
      // a later paste can resurrect old asset metadata even though clips/
      // audioClips were rebased — so rebase it with the same lookups.
      const rebaseCopiedClip = (
        copiedClip: TimelineState['copiedClip'],
        copiedClipType: TimelineState['copiedClipType'],
      ): TimelineState['copiedClip'] => {
        if (!copiedClip) return copiedClip
        if (copiedClipType === 'audio') {
          const ref = matchingRef(copiedClip, audioRefById, audioRefBySourceUnique)
          if (!ref) return copiedClip
          return {
            ...copiedClip,
            sourcePath: ref.sourcePath,
            label: ref.label,
            audioKind: ref.audioKind,
            originalDuration: ref.originalDuration,
            effectiveDuration: recomputeEffectiveDuration(
              ref.originalDuration,
              copiedClip.trimStart,
              copiedClip.trimEnd,
              copiedClip.speed,
            ),
          }
        }
        if (copiedClipType !== 'video') return copiedClip
        const ref = matchingRef(copiedClip, videoRefById, videoRefBySourceUnique)
        if (!ref) return copiedClip
        return {
          ...copiedClip,
          sourcePath: ref.sourcePath,
          label: ref.label,
          originalDuration: ref.originalDuration,
          effectiveDuration: recomputeEffectiveDuration(
            ref.originalDuration,
            copiedClip.trimStart,
            copiedClip.trimEnd,
            copiedClip.speed,
          ),
        }
      }
      // Shared snapshot rebaser — reused for history entries AND the preserved
      // trim-preview baseline so a mid-drag refresh never leaves stale metadata.
      const rebaseTimelineSnapshot = (snapshot: TimelineState): TimelineState => {
        const clips = snapshot.clips.map((clip) => {
          const ref = matchingRef(clip, videoRefById, videoRefBySourceUnique)
          if (!ref) return clip
          return {
            ...clip,
            sourcePath: ref.sourcePath,
            label: ref.label,
            originalDuration: ref.originalDuration,
            effectiveDuration: recomputeEffectiveDuration(
              ref.originalDuration,
              clip.trimStart,
              clip.trimEnd,
              clip.speed,
            ),
          }
        })
        const audioClips = snapshot.audioClips.map((clip) => {
          const ref = matchingRef(clip, audioRefById, audioRefBySourceUnique)
          if (!ref) return clip
          return {
            ...clip,
            sourcePath: ref.sourcePath,
            label: ref.label,
            audioKind: ref.audioKind,
            originalDuration: ref.originalDuration,
            effectiveDuration: recomputeEffectiveDuration(
              ref.originalDuration,
              clip.trimStart,
              clip.trimEnd,
              clip.speed,
            ),
          }
        })
        // Metadata refresh must not shift authored offsets. User edit actions
        // still enforce overlap invariants when they create or move clips.
        return withDuration({
          ...snapshot,
          clips: action.reconcileMagnet && snapshot.magnetEnabled ? packMainTrack(clips) : clips,
          audioClips,
          copiedClip: rebaseCopiedClip(snapshot.copiedClip, snapshot.copiedClipType),
        })
      }
      const rebaseHistoryEntry = (entry: TimelineHistoryEntry): TimelineHistoryEntry => ({
        ...entry,
        state: rebaseTimelineSnapshot(entry.state),
      })
      const rebasedPast = past.map(rebaseHistoryEntry)
      const rebasedFuture = future.map(rebaseHistoryEntry)
      undoHistoryLog({
        event: 'refresh-present',
        reason: 'metadata-refresh',
        pastDepthBefore: past.length,
        pastDepthAfter: rebasedPast.length,
        trimPreviewBaselinePreserved: state.trimPreviewBaseline != null,
      })
      return {
        present: refreshedPresent,
        past: rebasedPast,
        future: rebasedFuture,
        editRevision,
        // T279076566 (CodeRabbit): PRESERVE (and rebase) the active trim-preview
        // baseline so a refresh landing mid trim drag doesn't make the trim
        // commit push the preview state as the undo baseline.
        trimPreviewBaseline: state.trimPreviewBaseline
          ? rebaseTimelineSnapshot(state.trimPreviewBaseline)
          : null,
        trimPreviewBaselineClipId: state.trimPreviewBaselineClipId ?? null,
        trimPreviewBaselineClipType: state.trimPreviewBaselineClipType ?? null,
      }
    }

    default:
      return state
  }
}

/**
 * Hook for managing timeline state with undo/redo
 */
export function useTimelineState(initialState?: TimelineState, context?: { projectId: string; nodeId: string }) {
  const [state, dispatch] = useReducer(timelineReducer, {
    present: initialState ? withDuration(initialState) : createInitialState(),
    past: [],
    future: [],
    editRevision: 0,
  })

  // Observe committed state, not speculative reducer invocations (StrictMode
  // invokes reducers twice). Scalar deltas survive NAD's array sanitization.
  const previousCommit = useRef(state)
  const revision = useRef(0)
  const editorInstance = useId()
  const projectId = context?.projectId
  const nodeId = context?.nodeId
  useEffect(() => {
    const previous = previousCommit.current
    previousCommit.current = state
    if (!overhaulLog.enabled && !import.meta.env?.VITE_NAD_DEBUG) return
    if (!projectId || !nodeId || (previous.present.clips === state.present.clips
      && previous.present.audioClips === state.present.audioClips)) return
    revision.current += 1
    const undo = state.future.length > previous.future.length
    const latest = state.past[state.past.length - 1]
    const previousLatest = previous.past[previous.past.length - 1]
    const redo = latest?.description === 'redo' && state.future.length === previous.future.length - 1
    const edited = latest && latest.timestamp !== previousLatest?.timestamp
    const action = undo ? previous.past[previous.past.length - 1]?.description
      : state.past[state.past.length - 1]?.description
    const operation = undo ? 'undo' : redo ? 'redo' : edited ? 'edit' : 'load-or-refresh'
    const common = { project_id: projectId, node_id: nodeId,
      trace_id: `timeline-edit:${nodeId}:${editorInstance}:${revision.current}`, revision: revision.current,
      operation, action: operation === 'load-or-refresh' ? 'Load or refresh timeline' : action,
      playhead: previous.present.playheadPosition,
      selection_count: previous.present.selectedClipIds.size, magnetic: state.present.magnetEnabled }
    const before = [...previous.present.clips, ...previous.present.audioClips]
    const after = [...state.present.clips, ...state.present.audioClips]
    for (const clip of before) {
      if (!after.some((item) => item.id === clip.id)) {
        overhaulLog({ ...common, event: 'clip-removed', clip_id: clip.id })
      }
    }
    for (const clip of after) {
      const old = before.find((item) => item.id === clip.id)
      if (old && JSON.stringify(old) === JSON.stringify(clip)) continue
      overhaulLog({ ...common, event: old ? 'clip-edit-committed' : 'clip-added', clip_id: clip.id,
        before_start: old?.startOffset ?? null, start: clip.startOffset,
        before_duration: old?.effectiveDuration ?? null, duration: clip.effectiveDuration,
        before_speed: old?.speed ?? null, speed: clip.speed,
        trim_start: clip.trimStart, trim_end: clip.trimEnd,
        track: clip.trackIndex })
      if (old?.audioFadeInS !== clip.audioFadeInS || old?.audioFadeOutS !== clip.audioFadeOutS) {
        overhaulLog({ ...common, event: 'audio-fades-committed', clip_id: clip.id,
          fade_in: clip.audioFadeInS ?? 0, fade_out: clip.audioFadeOutS ?? 0 })
      }
      if (old && old.sourcePath !== clip.sourcePath) {
        overhaulLog({ ...common, event: 'clip-source-committed', clip_id: clip.id,
          before_source: old.sourcePath, source_path: clip.sourcePath })
      }
      const video = state.present.clips.find((item) => item.id === clip.id)
      if (video) {
        const oldVideo = previous.present.clips.find((item) => item.id === clip.id)
        if (oldVideo?.speedCurve !== video.speedCurve) {
          overhaulLog({ ...common, event: 'speed-curve-committed', clip_id: clip.id,
            point_count: video.speedCurve?.points.length ?? 0, duration: video.effectiveDuration,
            original_source: video.speedCurve?.source_path ?? null })
        }
        if (JSON.stringify(oldVideo?.adjustments) !== JSON.stringify(video.adjustments)
          || oldVideo?.fadeInS !== video.fadeInS || oldVideo?.fadeOutS !== video.fadeOutS
          || oldVideo?.fadeColor !== video.fadeColor) {
          overhaulLog({ ...common, event: 'clip-appearance-committed', clip_id: clip.id,
            ...video.adjustments, fade_in: video.fadeInS ?? 0, fade_out: video.fadeOutS ?? 0,
            fade_color: video.fadeColor ?? 'black' })
        }
      }
    }
  }, [state, projectId, nodeId, editorInstance])

  // Action creators
  const addClip = useCallback((clip: TimelineClip) => {
    dispatch({ type: 'ADD_CLIP', clip })
  }, [])

  const removeClip = useCallback((clipId: string) => {
    dispatch({ type: 'REMOVE_CLIP', clipId })
  }, [])

  const reorderClip = useCallback((clipId: string, newOrder: number) => {
    dispatch({ type: 'REORDER_CLIP', clipId, newOrder })
  }, [])

  const splitClip = useCallback((clipId: string, splitTime: number) => {
    dispatch({ type: 'SPLIT_CLIP', clipId, splitTime })
  }, [])

  const splitClipByDialogue = useCallback((clipId: string, segments: Array<{ start_s: number; end_s: number }>) => {
    const clip = state.present.clips.find((candidate) => candidate.id === clipId)
    const splitClips = clip ? buildDialogueSplitVideoClips(clip, segments) : []
    dispatch({ type: 'SPLIT_CLIP_BY_DIALOGUE', clipId, segments })
    return {
      changed: splitClips.length > 1,
      clipCount: splitClips.length,
    }
  }, [state.present.clips])

  const splitAudioClipByDialogue = useCallback((clipId: string, segments: Array<{ start_s: number; end_s: number }>) => {
    const clip = state.present.audioClips.find((candidate) => candidate.id === clipId)
    const splitClips = clip ? buildDialogueSplitAudioClips(clip, segments) : []
    dispatch({ type: 'SPLIT_AUDIO_CLIP_BY_DIALOGUE', clipId, segments })
    return {
      changed: splitClips.length > 1,
      clipCount: splitClips.length,
    }
  }, [state.present.audioClips])

  const trimClip = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_CLIP', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const trimClipPreview = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_CLIP_PREVIEW', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const setClipOffset = useCallback((clipId: string, startOffset: number) => {
    dispatch({ type: 'SET_CLIP_OFFSET', clipId, startOffset })
  }, [])

  const setClipTrackIndex = useCallback((clipId: string, trackIndex: number) => {
    dispatch({ type: 'SET_CLIP_TRACK_INDEX', clipId, trackIndex })
  }, [])

  const selectClip = useCallback((clipId: string | null, clipType?: 'video' | 'audio' | null) => {
    dispatch({ type: 'SELECT_CLIP', clipId, clipType })
  }, [])

  const toggleClipSelection = useCallback((clipId: string) => {
    dispatch({ type: 'TOGGLE_CLIP_SELECTION', clipId })
  }, [])

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' })
  }, [])

  const rippleDeleteSelected = useCallback(() => {
    dispatch({ type: 'RIPPLE_DELETE_SELECTED' })
  }, [])

  const multiDragOffset = useCallback((deltaSec: number, trackDelta?: number) => {
    dispatch({ type: 'MULTI_DRAG_OFFSET', deltaSec, trackDelta })
  }, [])

  const setPlayhead = useCallback((position: number, playbackDuration?: number) => {
    dispatch({ type: 'SET_PLAYHEAD', position, playbackDuration })
  }, [])

  const setZoom = useCallback((zoomLevel: number) => {
    dispatch({ type: 'SET_ZOOM', zoomLevel })
  }, [])

  const play = useCallback(() => {
    dispatch({ type: 'PLAY' })
  }, [])

  const pause = useCallback(() => {
    dispatch({ type: 'PAUSE' })
  }, [])

  const togglePlayPause = useCallback(() => {
    dispatch({ type: state.present.isPlaying ? 'PAUSE' : 'PLAY' })
  }, [state.present.isPlaying])

  const addAudioClip = useCallback((clip: TimelineAudioClip) => {
    dispatch({ type: 'ADD_AUDIO_CLIP', clip })
  }, [])

  const addAudioClips = useCallback((clips: TimelineAudioClip[]) => {
    dispatch({ type: 'ADD_AUDIO_CLIPS', clips })
  }, [])

  const extractNativeVideoAudio = useCallback((clipId: string) => {
    dispatch({ type: 'EXTRACT_NATIVE_VIDEO_AUDIO', clipId })
  }, [])

  const applyDerivedAudioClips = useCallback((
    sourceClipId: string,
    sourceClipType: 'video' | 'audio',
    actionId: TimelineAudioActionId,
    clips: TimelineAudioClip[],
    description?: string,
  ) => {
    dispatch({ type: 'APPLY_DERIVED_AUDIO_IN_PLACE', actionId, sourceClipId, sourceClipType, clips, description })
  }, [])

  const removeAudioClip = useCallback((clipId: string) => {
    dispatch({ type: 'REMOVE_AUDIO_CLIP', clipId })
  }, [])

  const setAudioVolume = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_AUDIO_VOLUME', clipId, volume })
  }, [])

  const setAudioVolumePreview = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_AUDIO_VOLUME_PREVIEW', clipId, volume })
  }, [])

  const commitAudioVolume = useCallback((clipId: string, volume: number, baselineVolume: number) => {
    dispatch({ type: 'COMMIT_AUDIO_VOLUME', clipId, volume, baselineVolume })
  }, [])

  const toggleAudioMute = useCallback((clipId: string) => {
    dispatch({ type: 'TOGGLE_AUDIO_MUTE', clipId })
  }, [])

  const setAudioOffset = useCallback((clipId: string, startOffset: number) => {
    dispatch({ type: 'SET_AUDIO_OFFSET', clipId, startOffset })
  }, [])

  const setAudioTrackIndex = useCallback((clipId: string, trackIndex: number) => {
    dispatch({ type: 'SET_AUDIO_TRACK_INDEX', clipId, trackIndex })
  }, [])

  const trimAudioClip = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_AUDIO_CLIP', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const trimAudioClipPreview = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_AUDIO_CLIP_PREVIEW', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const setClipSpeed = useCallback((clipId: string, speed: number) => {
    dispatch({ type: 'SET_CLIP_SPEED', clipId, speed })
  }, [])

  const setClipVisuals = useCallback((clipId: string, adjustments: TimelineClip['adjustments'], fadeColor?: TimelineClip['fadeColor']) => {
    dispatch({ type: 'SET_CLIP_VISUALS', clipId, adjustments, fadeColor })
  }, [])
  const replaceClipSource = useCallback((clipId: string, sourcePath: string, duration: number, mode: 'reverse' | 'freeze' | 'speed_curve', speedCurve?: TimelineClip['speedCurve']) => {
    dispatch({ type: 'REPLACE_CLIP_SOURCE', clipId, sourcePath, duration, mode, speedCurve })
  }, [])

  const setAudioFades = useCallback((clipId: string, fadeIn: number, fadeOut: number) => {
    dispatch({ type: 'SET_AUDIO_FADES', clipId, fadeIn, fadeOut })
  }, [])
  const cutAtPlayhead = useCallback((side: 'left' | 'right') => {
    dispatch({ type: 'CUT_AT_PLAYHEAD', side })
  }, [])
  const setTransition = useCallback((leftId: string, rightId: string, color: 'black' | 'white' | null) => {
    dispatch({ type: 'SET_TRANSITION', leftId, rightId, color })
  }, [])

  const setClipVolume = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_CLIP_VOLUME', clipId, volume })
  }, [])

  const setClipVolumePreview = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_CLIP_VOLUME_PREVIEW', clipId, volume })
  }, [])

  const commitClipVolume = useCallback((clipId: string, volume: number, baselineVolume: number) => {
    dispatch({ type: 'COMMIT_CLIP_VOLUME', clipId, volume, baselineVolume })
  }, [])

  const setClipFadeOut = useCallback((clipId: string, fadeOutS: number) => {
    dispatch({ type: 'SET_CLIP_FADE_OUT', clipId, fadeOutS })
  }, [])

  const setClipFadeIn = useCallback((clipId: string, fadeInS: number) => {
    dispatch({ type: 'SET_CLIP_FADE_IN', clipId, fadeInS })
  }, [])

  const normalizeTimelineOrigin = useCallback(() => {
    dispatch({ type: 'NORMALIZE_TIMELINE_ORIGIN' })
  }, [])

  const closeAllGaps = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_GAPS' })
  }, [])

  const toggleClipMute = useCallback((clipId: string) => {
    dispatch({ type: 'TOGGLE_CLIP_MUTE', clipId })
  }, [])

  const setAudioSpeed = useCallback((clipId: string, speed: number) => {
    dispatch({ type: 'SET_AUDIO_SPEED', clipId, speed })
  }, [])

  const splitAudioClip = useCallback((clipId: string, splitTime: number) => {
    dispatch({ type: 'SPLIT_AUDIO_CLIP', clipId, splitTime })
  }, [])

  const reorderAudioClip = useCallback((clipId: string, newOrder: number) => {
    dispatch({ type: 'REORDER_AUDIO_CLIP', clipId, newOrder })
  }, [])

  const copyClip = useCallback(() => {
    dispatch({ type: 'COPY_CLIP' })
  }, [])

  const pasteClip = useCallback(() => {
    dispatch({ type: 'PASTE_CLIP' })
  }, [])

  const deleteSelectedClip = useCallback(() => {
    dispatch({ type: 'DELETE_SELECTED_CLIP' })
  }, [])

  const splitSelectedClip = useCallback(() => {
    dispatch({ type: 'SPLIT_SELECTED_CLIP' })
  }, [])

  const addVideoTrack = useCallback(() => {
    dispatch({ type: 'ADD_VIDEO_TRACK' })
  }, [])

  const addAudioTrack = useCallback(() => {
    dispatch({ type: 'ADD_AUDIO_TRACK' })
  }, [])

  // RFC-0631 round-3 (Issue 5): Remove a sub-track. The caller is
  // responsible for the user confirmation when clips exist on the track —
  // the reducer guards on `deleteClips` so accidental dispatches without a
  // confirm are a no-op.
  const removeVideoTrack = useCallback(
    (trackIndex: number, deleteClips: boolean) => {
      trackMgmtLog({
        event: 'track-remove-requested',
        kind: 'video',
        trackIndex,
        deleteClips,
      })
      dispatch({ type: 'REMOVE_VIDEO_TRACK', trackIndex, deleteClips })
    },
    [],
  )

  const removeAudioTrack = useCallback(
    (trackIndex: number, deleteClips: boolean) => {
      trackMgmtLog({
        event: 'track-remove-requested',
        kind: 'audio',
        trackIndex,
        deleteClips,
      })
      dispatch({ type: 'REMOVE_AUDIO_TRACK', trackIndex, deleteClips })
    },
    [],
  )

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' })
  }, [])

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' })
  }, [])

  const restoreState = useCallback((newState: TimelineState, options?: { preserveEditRevision?: boolean }) => {
    dispatch({ type: 'RESTORE_STATE', state: newState, preserveEditRevision: options?.preserveEditRevision })
  }, [])

  // T279076566: history-preserving refresh for background/system metadata
  // updates. Mirrors restoreState but keeps the undo/redo stack intact.
  const setTextOverlays = useCallback((overlays: NonNullable<TimelineState['textOverlays']>, format: NonNullable<TimelineState['textFormat']>) => {
    dispatch({ type: 'SET_TEXT_OVERLAYS', overlays, format })
  }, [])
  const refreshTimelineState = useCallback((newState: TimelineState, reconcileMagnet = false) => {
    dispatch({ type: 'REFRESH_PRESENT', state: newState, reconcileMagnet })
  }, [])

  const toggleTrackMute = useCallback(
    (trackKind: 'video' | 'audio', trackIndex: number) => {
      dispatch({ type: 'TOGGLE_TRACK_MUTE', trackKind, trackIndex })
    },
    [],
  )

  const setMagnetEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_MAGNET', enabled })
  }, [])

  const magnetPackTrack0 = useCallback(() => {
    dispatch({ type: 'MAGNET_PACK_TRACK0' })
  }, [])

  // Derived state
  const canUndo = state.past.length > 0
  const canRedo = state.future.length > 0

  return useMemo(
    () => ({
      // Current state
      timelineState: state.present,
      editRevision: state.editRevision ?? 0,

      // Actions
      addClip,
      removeClip,
      reorderClip,
      splitClip,
      splitClipByDialogue,
      splitAudioClipByDialogue,
      trimClip,
      trimClipPreview,
      setClipOffset,
      setClipTrackIndex,
      selectClip,
      toggleClipSelection,
      clearSelection,
      rippleDeleteSelected,
      multiDragOffset,
      setPlayhead,
      setZoom,
      play,
      pause,
      togglePlayPause,
      addAudioClip,
      addAudioClips,
      extractNativeVideoAudio,
      applyDerivedAudioClips,
      removeAudioClip,
      setAudioVolume,
      setAudioVolumePreview,
      commitAudioVolume,
      toggleAudioMute,
      setAudioOffset,
      setAudioTrackIndex,
      trimAudioClip,
      trimAudioClipPreview,
      setClipSpeed,
      setClipVisuals,
      replaceClipSource,
      setAudioFades,
      cutAtPlayhead,
      setTransition,
      setClipVolume,
      setClipVolumePreview,
      commitClipVolume,
      setClipFadeOut,
      setClipFadeIn,
      normalizeTimelineOrigin,
      closeAllGaps,
      toggleClipMute,
      setAudioSpeed,
      splitAudioClip,
      reorderAudioClip,
      copyClip,
      pasteClip,
      deleteSelectedClip,
      splitSelectedClip,
      addVideoTrack,
      addAudioTrack,
      removeVideoTrack,
      removeAudioTrack,
      undo,
      redo,
      restoreState,
      refreshTimelineState,
      setTextOverlays,
      toggleTrackMute,
      setMagnetEnabled,
      magnetPackTrack0,

      // Undo/Redo state
      canUndo,
      canRedo,
    }),
    [
      state.present,
      state.editRevision,
      addClip,
      removeClip,
      reorderClip,
      splitClip,
      splitClipByDialogue,
      splitAudioClipByDialogue,
      trimClip,
      trimClipPreview,
      setClipOffset,
      setClipTrackIndex,
      selectClip,
      toggleClipSelection,
      clearSelection,
      rippleDeleteSelected,
      multiDragOffset,
      setPlayhead,
      setZoom,
      play,
      pause,
      togglePlayPause,
      addAudioClip,
      addAudioClips,
      extractNativeVideoAudio,
      applyDerivedAudioClips,
      removeAudioClip,
      setAudioVolume,
      setAudioVolumePreview,
      commitAudioVolume,
      toggleAudioMute,
      setAudioOffset,
      setAudioTrackIndex,
      trimAudioClip,
      trimAudioClipPreview,
      setClipSpeed,
      setClipVisuals,
      replaceClipSource,
      setAudioFades,
      cutAtPlayhead,
      setTransition,
      setClipVolume,
      setClipVolumePreview,
      commitClipVolume,
      setClipFadeOut,
      setClipFadeIn,
      normalizeTimelineOrigin,
      closeAllGaps,
      toggleClipMute,
      setAudioSpeed,
      splitAudioClip,
      reorderAudioClip,
      copyClip,
      pasteClip,
      deleteSelectedClip,
      splitSelectedClip,
      addVideoTrack,
      addAudioTrack,
      removeVideoTrack,
      removeAudioTrack,
      undo,
      redo,
      restoreState,
      refreshTimelineState,
      setTextOverlays,
      toggleTrackMute,
      setMagnetEnabled,
      magnetPackTrack0,
      canUndo,
      canRedo,
    ]
  )
}
